import { useEffect, useState, useCallback, useRef } from 'react';
import { planRowDate } from '../utils/planRowDate';
import { useFavorites } from '../contexts/FavoritesContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ShoppingBag, Plus, Sparkles, RefreshCw, Trash2, MoreHorizontal, AlertCircle, Target, Ban, Check, RotateCcw, X, Lock, ChefHat, Package, Edit2, Wand2, Filter, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import PlanFoodLauncher from '../components/PlanFoodLauncher';
import MealCard from '../components/MealCard';
import MealImage from '../components/MealImage';
import { PairSwapDialog } from '../components/PairSwapDialog';
import { buildRegenSelection, isLinkedRow } from '../utils/regenSelection';
import { sumDayMacros } from '../utils/planMacroTotals';
import { classifyAutoRegenResult, firstIncompleteReason, retainedOnlyMessage } from '../utils/regenResultClassifier';
import { useLatestRequest } from '../utils/requestLifecycle';
import { useScreenPerf } from '../utils/perf';
import { friendlyRegenerateReasonText } from './planReviewRegenerateDayFailure';
import { recordTasteSignal, recordRegenTasteSignals, TASTE_ACTIONS } from '../utils/tasteSignals';
import JumpToPicker from '../components/JumpToPicker';
import CookingPreferencesSelector from '../components/CookingPreferencesSelector';
import PantrySelector from '../components/PantrySelector';
import { planningAPI, mealPlanAPI, smartFillAPI, profileAPI, pantryAPI } from '../utils/api';
import { getMacroStatus, getStatusDisplay, getMacroBarColor, getToleranceRanges } from '../utils/macroStatus';
import { MacroBar } from '../components/MacroBar';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getWeekDatesWithOffset, getWeekRangeText, getTodayString, formatDateString } from '../utils/dateUtils';
import { readPlanLocation, planSearchParams, planPath, weekOffsetFor, addDays, buildUrl, startFlowState } from '../utils/planLocation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { FEATURES } from '../services/subscriptionService';
import { FIX_MODE_KEY, setFixModeAdvancePending, consumeFixModeAdvanceSignal, clearFixModeState } from '../utils/fixModeAdvanceSignal';
import { isWeekOnTrack } from '../utils/fixDaysWeekStatus';
import NutritionTargetsSetupPrompt from '../components/NutritionTargetsSetupPrompt';

function PlanningHub() {
  var navigate = useNavigate();
  var location = useLocation();
  var [searchParams, setSearchParams] = useSearchParams();
  var { canAccess, requireFeature, isFree } = useSubscriptionContext();

  // Where the user is on Plan lives in the URL (see utils/planLocation.js):
  // /plan?view=daily|weekly&date=YYYY-MM-DD. A refresh, or a return from any
  // child flow, restores that exact Daily date / Weekly week. `selectedDate` is
  // the anchor and Weekly shows the week containing it, so weekOffset is
  // derived from it and can never drift away from the selected day.
  var today = getTodayString();
  var planLoc = readPlanLocation(searchParams, today);
  var mode = planLoc.view; // 'daily' | 'weekly'
  var selectedDate = planLoc.date;
  var weekOffset = weekOffsetFor(selectedDate, today);

  // Every in-page move (day/week arrows, Return to Today/This Week, jump
  // picker, day taps, Fix Days) is ONE URL write. It replaces the current entry
  // so Back still leaves Plan instead of stepping through every day viewed.
  function setPlanLocation(next) {
    setSearchParams(planSearchParams(next), { replace: true, state: location.state });
  }

  // Switch Daily <-> Weekly around the same anchor date.
  function setMode(nextView) {
    setPlanLocation({ view: nextView, date: selectedDate });
  }

  // The exact current location, handed to every child flow as its return.
  var planReturn = planPath({ view: mode, date: selectedDate });

  // Data
  var [summary, setSummary] = useState(null);
  var [groceryOpen, setGroceryOpen] = useState(false);
  var [groceryRefresh, setGroceryRefresh] = useState(0);
  var [groceryResult, setGroceryResult] = useState(null);
  var groceryLoadedKey = useRef(null);
  var groceryLoad = useLatestRequest();
  var groceryKey = JSON.stringify([mode, selectedDate, weekOffset, isFree, groceryRefresh]);
  var groceryCurrent = groceryResult && groceryResult.key === groceryKey;
  var groceryList = groceryCurrent ? groceryResult.items : [];
  var groceryLoading = !groceryCurrent || groceryResult.loading;
  var groceryError = groceryCurrent ? groceryResult.error : null;
  var [loading, setLoading] = useState(true);
  var planLoad = useLatestRequest();
  // summaryError: captured when the planning summary fetch fails with a
  // non-subscription error. Rendered as an explicit "Couldn't load your
  // plan" card so users never see a misleading silent "No meals" fallback
  // when the real cause is an API outage. Shape:
  //   { status: number|null, code: string|null, message: string }
  var [summaryError, setSummaryError] = useState(null);
  // [PERF] temporary screen timing (MOUNT -> READY).
  useScreenPerf('PLAN', !loading, summaryError);
  var [showSummaryErrorDetails, setShowSummaryErrorDetails] = useState(false);
  // Load checked items from localStorage synchronously on init
  var [checkedItems, setCheckedItems] = useState(function() {
    var GROCERY_CHECKED_KEY = 'fitxess_grocery_checked';
    var saved = localStorage.getItem(GROCERY_CHECKED_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  });
  var { isFavorite: favHas, toggleFavorite: ctxToggleFavorite } = useFavorites();
  var [actionLoading, setActionLoading] = useState(null);
  var [showPlanLauncher, setShowPlanLauncher] = useState(false);
  // Guided Fix Days: when set, the shared PlanFoodLauncher opens with this
  // date already locked (skips Step 1 date/scope selection). Every other
  // caller of setShowPlanLauncher(true) leaves this null, so normal Home/
  // Daily/Weekly/Custom launcher behavior is unaffected. Always cleared on
  // close so it can never leak into the next, unrelated launcher open.
  var [planLauncherPresetDate, setPlanLauncherPresetDate] = useState(null);
  // Guided Fix Days: the preset date's own summary when it already has saved
  // rows (null for an empty day). Its presence routes the launcher's Smart
  // Fill to Regenerate Day instead of the blank-day generator. Cleared on
  // close together with the preset date.
  var [planLauncherPresetSummary, setPlanLauncherPresetSummary] = useState(null);
  var [clearConfirm, setClearConfirm] = useState(null); // 'day' | 'week' | null
  var [leftoverRemoveConfirm, setLeftoverRemoveConfirm] = useState(null); // {planId, mealName, role} | null
  var [replacePairConfirm, setReplacePairConfirm] = useState(null); // legacy (unused)
  var [pairSwapContext, setPairSwapContext] = useState(null); // shared Replace Both dialog context
  var [clearing, setClearing] = useState(false);
  var clearOpRef = useRef(null); // stable idempotency key for a clear attempt
  // Track skipped meal slots: { '2026-02-06-Breakfast': true }
  var [skippedSlots, setSkippedSlots] = useState({});
  
  // Expandable state for meal groups
  var [expandedMealTypes, setExpandedMealTypes] = useState({});
  // Expandable state for days in weekly view
  var [expandedDays, setExpandedDays] = useState({});
  // Guided Fix Plan flow state — persisted across navigation (PlanReview etc.) via sessionStorage
  function loadFixMode() {
    try {
      var raw = sessionStorage.getItem(FIX_MODE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }
  var initialFixMode = loadFixMode();
  var [fixModeActive, setFixModeActive] = useState(initialFixMode ? initialFixMode.active : false);
  var [fixModeQueue, setFixModeQueue] = useState(initialFixMode ? initialFixMode.queue : []); // array of dateStrs
  var [fixModeIndex, setFixModeIndex] = useState(initialFixMode ? initialFixMode.index : 0);
  // True once the CURRENT queue target was actually generated/saved. It only
  // changes the banner (the day's result is shown with an explicit Next
  // problem day / Finish); it never advances the queue by itself.
  var [fixModeHandled, setFixModeHandled] = useState(initialFixMode ? !!initialFixMode.handled : false);
  // A planned queued day waiting for the page summary to hold its rows before
  // the Regenerate Day modal opens for it (see openFixDay).
  var [pendingFixRegenDate, setPendingFixRegenDate] = useState(null);

  // Keep sessionStorage in sync so fix mode survives navigation to /plan/review and back.
  useEffect(function() {
    if (fixModeActive && fixModeQueue.length > 0) {
      sessionStorage.setItem(FIX_MODE_KEY, JSON.stringify({
        active: fixModeActive,
        queue: fixModeQueue,
        index: fixModeIndex,
        handled: fixModeHandled
      }));
    } else {
      sessionStorage.removeItem(FIX_MODE_KEY);
    }
  }, [fixModeActive, fixModeQueue, fixModeIndex, fixModeHandled]);
  // Regenerate modal state
  var [showRegenerateModal, setShowRegenerateModal] = useState(false);
  // Modal opens directly into the AI flow. The legacy 'confirm' step
  // (Browse vs AI choice) was removed — Regenerate now always means AI
  // regeneration. Users who want manual selection use Plan Food.
  var [regenerateStep, setRegenerateStep] = useState('keep-selection'); // 'keep-selection', 'preferences', 'cooking', 'pantry', 'day-walkthrough', 'generating'
  var [regenerating, setRegenerating] = useState(false);
  
  // Keep selections state for regenerate
  var [mealsToKeep, setMealsToKeep] = useState([]); // Array of plan_ids to keep
  
  // Cooking preferences state for regenerate
  var [selectedCookware, setSelectedCookware] = useState([]);
  var [selectedCookTime, setSelectedCookTime] = useState(null);
  var [pantryItems, setPantryItems] = useState([]);
  var [fullPantryData, setFullPantryData] = useState({});
  
  // Filters state for regenerate
  var [showFiltersPanel, setShowFiltersPanel] = useState(false);
  var [selectedTags, setSelectedTags] = useState([]);
  var [selectedCuisine, setSelectedCuisine] = useState([]);
  // LEFTOVERS-LITE: server flag + per-plan opt-in (default OFF).
  var [leftoversFlagEnabled, setLeftoversFlagEnabled] = useState(false);
  var [includeLeftovers, setIncludeLeftovers] = useState(false);
  var [leftoversTouched, setLeftoversTouched] = useState(false);
  
  // Weekly walkthrough state (for day-by-day selection in weekly mode)
  var [weeklyWalkthroughDayIndex, setWeeklyWalkthroughDayIndex] = useState(0);
  var [weeklyMealsToKeepByDate, setWeeklyMealsToKeepByDate] = useState({});
  
  // Per-day regeneration in weekly view (regenerate single day while in weekly mode)
  var [regenerateSingleDay, setRegenerateSingleDay] = useState(null); // Date string of day being regenerated
  
  // New modal states for the redesigned button flow
  var [showPlanModal, setShowPlanModal] = useState(false);
  var [showAddMealModal, setShowAddMealModal] = useState(false);
  var [addMealStep, setAddMealStep] = useState(1); // 1=date, 'datePicker'=custom
  var [addMealDate, setAddMealDate] = useState(null);
  var [planModalOption, setPlanModalOption] = useState(null); // 'thisWeek' | 'custom'
  var [customStartDate, setCustomStartDate] = useState('');
  var [customEndDate, setCustomEndDate] = useState('');
  var [planningInProgress, setPlanningInProgress] = useState(false);
  // CUSTOM RANGE: when set, the regenerate/generate engine (keep-selection
  // walkthrough + the actual generation request) targets exactly this date
  // list instead of the currently viewed week. null = no override in effect
  // (all normal Plan This Week / Regenerate behavior is untouched). Cleared
  // whenever the Regenerate Modal closes so it can never leak into a later
  // normal operation.
  var [customPlanningDates, setCustomPlanningDates] = useState(null);
  // CUSTOM RANGE: existing-meals-by-date lookup for the active custom range
  // specifically. `summary` (and getMealsForDate()) only ever holds data for
  // the currently VIEWED week, which a custom range may not overlap at all —
  // without this, the keep-selection walkthrough would silently treat any
  // out-of-view custom date as having zero existing meals. Fetched once when
  // Custom is confirmed; null when no custom range is active (all normal
  // behavior reads getMealsForDate()/`summary` exactly as before).
  var [customRangeMealsByDate, setCustomRangeMealsByDate] = useState(null);

  // Jump-to picker (date for daily mode, week list for weekly mode)
  var [showJumpPicker, setShowJumpPicker] = useState(false);

  // The location is read from the URL on every render, so a same-page
  // navigation (e.g. the Plan Food launcher showing the day it just planned)
  // needs no sync. Legacy links (?date= alone, ?week=<n>) are rewritten once
  // to the canonical form so every later refresh / return reads one
  // unambiguous location.
  useEffect(function() {
    if (planLoc.isLegacy) setPlanLocation({ view: planLoc.view, date: planLoc.date });
  }, [planLoc.isLegacy, planLoc.view, planLoc.date]); // eslint-disable-line react-hooks/exhaustive-deps

  // LEFTOVERS-LITE: fetch the server feature flag once on mount so the
  // Before You Plan option is only shown/enabled when the backend supports it.
  useEffect(function() {
    var cancelled = false;
    smartFillAPI.getFeatureFlags()
      .then(function(res) {
        if (!cancelled) setLeftoversFlagEnabled(!!(res && res.data && res.data.leftovers_lite_enabled));
      })
      .catch(function() { if (!cancelled) setLeftoversFlagEnabled(false); });
    return function() { cancelled = true; };
  }, []);

  // LEFTOVERS-LITE: default the toggle ON for eligible multi-day planning, but
  // never clobber a manual choice within the same Before You Plan session.
  // The explicit >=2 check (on top of `mode !== 'daily'`) matters once a
  // custom range can put mode==='weekly' with as few as 1 date — normal
  // Plan This Week/Regenerate always have 7 dates here, so this is a no-op
  // for that unchanged path.
  useEffect(function() {
    if (!leftoversTouched) {
      setIncludeLeftovers(leftoversFlagEnabled && !regenerateSingleDay && mode !== 'daily' && getRegenDates().length >= 2);
    }
  }, [leftoversFlagEnabled, regenerateSingleDay, mode, leftoversTouched, customPlanningDates, weekOffset]);

  // LocalStorage key for grocery checked items (shared with Grocery page)
  var GROCERY_CHECKED_KEY = 'fitxess_grocery_checked';

  // Persist checked items to localStorage whenever they change
  useEffect(function() {
    localStorage.setItem(GROCERY_CHECKED_KEY, JSON.stringify(checkedItems));
  }, [checkedItems]);

  // P8: Favorites are account-backed via FavoritesContext (no localStorage).

  // Get dates for current week (Sunday start) - uses shared utility for consistency
  function getWeekDates(offset) {
    return getWeekDatesWithOffset(offset);
  }

  // CUSTOM RANGE: the single date source every generation/regeneration
  // decision point should read from. Prefers an explicit `datesOverride`
  // (passed only for the ONE synchronous decision made right after
  // confirming a custom range — see handlePlanCustomRange — since a state
  // setter's update is not visible in the current closure yet), else an
  // active custom range from state, else today's exact behavior (the
  // visible week) whenever neither is in effect. Intentionally NOT used by
  // visible-week rendering, the week header, Clear Week, the Add Meal
  // quick-date list, or normal summary fetching — those stay tied to
  // weekOffset regardless of any custom range.
  function getRegenDates(datesOverride) {
    return datesOverride || customPlanningDates || getWeekDates(weekOffset);
  }

  // Build an inclusive array of YYYY-MM-DD strings from start through end.
  // Noon parsing matches formatDate/formatShortDate below to avoid
  // timezone/DST off-by-one errors on date-only strings. Returns [] for an
  // invalid or inverted (end before start) range.
  function buildInclusiveDateRange(startStr, endStr) {
    var start = new Date(startStr + 'T12:00:00');
    var end = new Date(endStr + 'T12:00:00');
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];
    var out = [];
    var cur = new Date(start);
    while (cur <= end) {
      out.push(formatDateString(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }

  // Load pantry items from API on mount
  useEffect(function() {
    pantryAPI.getPantry()
      .then(function(response) {
        var data = response.data || {};
        setFullPantryData(data);  // Store full structure for later saving
        // Flatten all pantry items into a single array of names
        var allItems = [];
        ['proteins', 'pantry', 'dairy', 'produce', 'other'].forEach(function(cat) {
          var items = data[cat] || [];
          items.forEach(function(item) {
            if (item && item.name && typeof item.name === 'string') {
              allItems.push(item.name);
            }
          });
        });
        setPantryItems(allItems);
      })
      .catch(function(err) {
        console.error('Failed to fetch pantry:', err);
      });
  }, []);

  // Load cooking preferences from API on mount
  useEffect(function() {
    profileAPI.getCookingPreferences()
      .then(function(res) {
        var data = res.data;
        if (data.cookware && data.cookware.length > 0) {
          setSelectedCookware(data.cookware);
        }
        if (data.max_cook_time) {
          setSelectedCookTime(data.max_cook_time);
        }
      })
      .catch(function(err) {
        console.error('Failed to load cooking preferences:', err);
      });
  }, []);

  // Get dates based on mode
  function getActiveDates() {
    if (mode === 'daily') {
      return [selectedDate];
    } else {
      return getWeekDates(weekOffset);
    }
  }

  // Plan readiness depends only on its summary, never on groceries.
  var fetchData = useCallback(function(refreshGrocery) {
    var dates = getActiveDates();
    if (dates.length === 0) return;

    // Latest-request-wins: moving to another day/week aborts the older load,
    // whose late response can then never overwrite the newer selection.
    if (refreshGrocery !== false) setGroceryRefresh(function(n) { return n + 1; });
    var req = planLoad.begin();
    var opts = { signal: req.signal };
    setLoading(true);
    setSummaryError(null);

    var calls = [planningAPI.getSummary(dates, opts)];

    Promise.allSettled(calls)
    .then(function(results) {
      if (!req.isCurrent()) return;
      var sRes = results[0];

      if (sRes.status === 'fulfilled') {
        setSummary(sRes.value.data);
        setSummaryError(null);
      } else {
        console.error('[PlanningHub] summary fetch failed:', sRes.reason);
      }

      // Summary failure handling: distinguish subscription gating (handled
      // by the global 402 interceptor, no error card needed) from a real
      // outage (network, 5xx, parse error). Real outages drop into the
      // explicit error card below — NOT the silent "No meals" empty state.
      if (sRes.status === 'rejected') {
        var sResp = sRes.reason && sRes.reason.response;
        var sStatus = sResp && sResp.status;
        var sDetail = sResp && sResp.data && sResp.data.detail;
        var sCode = (sDetail && sDetail.error) || null;
        if (sCode !== 'subscription_required' && sCode !== 'daily_limit_reached') {
          setSummaryError({
            status: sStatus || null,
            code: sCode,
            message: (sDetail && sDetail.message) || (sRes.reason && sRes.reason.message) || 'Unknown error',
          });
          toast.error('Failed to load planning data');
        }
      }
    })
    .finally(function() {
      if (req.isCurrent()) setLoading(false);
    });
  }, [mode, selectedDate, weekOffset, isFree, planLoad]);

  useEffect(function() {
    fetchData(false);
  }, [fetchData]);

  useEffect(function() {
    if (groceryLoadedKey.current && groceryLoadedKey.current !== groceryKey) {
      groceryLoadedKey.current = null;
      setGroceryResult(null);
    }
    if (!groceryOpen || isFree || groceryLoadedKey.current === groceryKey) return;
    var req = groceryLoad.begin();
    var dates = mode === 'daily' ? [selectedDate] : getWeekDatesWithOffset(weekOffset);
    setGroceryResult({ key: groceryKey, items: [], loading: true, error: null });
    planningAPI.getGrocery(dates, { signal: req.signal })
      .then(function(res) {
        if (!req.isCurrent()) return;
        groceryLoadedKey.current = groceryKey;
        setGroceryResult({ key: groceryKey, items: res.data.ingredients || [], loading: false, error: null });
      })
      .catch(function(err) {
        if (!req.isCurrent()) return;
        var status = err.response && err.response.status;
        var detail = err.response && err.response.data && err.response.data.detail;
        var gated = status === 402 || status === 403 || (detail && (
          detail.error === 'subscription_required' || detail.error === 'daily_limit_reached'));
        setGroceryResult({ key: groceryKey, items: [], loading: false,
          error: { message: gated ? 'Grocery access is unavailable for this account.' : "Couldn't load groceries.",
                   retryable: !gated && status !== 401 } });
      });
    return groceryLoad.cancel;
  }, [groceryOpen, isFree, mode, selectedDate, weekOffset, groceryKey, groceryLoad]);

  // === Guided Fix Days: a saved/generated day is marked handled, never auto-advanced ===
  // The date-scoped one-shot signal (set by PlanReview.handleSave after a
  // successful Save, or by PlanFoodLauncher's onPlanGenerated after a
  // successful empty-day generation) only marks the CURRENT queue target as
  // handled and shows that SAME day with its actual result. It never moves
  // the queue or opens the next day's flow: the user advances only through
  // the banner's explicit Next problem day / Finish (advanceFixMode). Observed
  // data (e.g. a refresh where the day now reads On Track) is never a trigger.
  // Only a signal whose dates include the current target counts — an
  // unrelated generation while Fix Mode is active must not mark it handled
  // (see AGENTS.md pre-launch audit, finding FIXDAYS-1). This consumes it.
  useEffect(function() {
    if (!fixModeActive) return;
    if (!summary || !summary.meals_by_date) return;

    var targetDate = fixModeQueue[fixModeIndex];
    if (!targetDate) {
      // Stale/corrupt persisted queue — leave quietly (never claim the week
      // is on track without checking it).
      exitFixMode();
      return;
    }

    if (consumeFixModeAdvanceSignal(targetDate)) {
      setFixModeHandled(true);
      // Show the day they just fixed with its actual result (Review's Save
      // already returns to it; the launcher path may not have).
      setPlanLocation({ view: 'daily', date: targetDate });
    }
  }, [summary, fixModeActive, fixModeQueue, fixModeIndex, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open Regenerate Day for a planned queued day only once the page summary
  // actually holds that day's rows: handleRegenerateDayClick's start step and
  // the Keep any meals? list both read them from `summary`.
  useEffect(function() {
    if (!pendingFixRegenDate) return;
    if (!fixModeActive) { setPendingFixRegenDate(null); return; }
    if (getMealsForDate(pendingFixRegenDate).length === 0) return;
    var regenDate = pendingFixRegenDate;
    setPendingFixRegenDate(null);
    handleRegenerateDayClick(regenDate);
  }, [summary, pendingFixRegenDate, fixModeActive]);

  // In-flight openFixDay request: a newer request (or leaving Fix Days)
  // invalidates an older one, and Next/Finish are ignored while one is pending
  // so a double tap can't skip a queued day.
  var fixRouteRef = useRef({ token: 0, pending: false });

  // Every queued Fix Days date opens the Plan Food launcher's method choice
  // (Smart Fill / Step-by-Step / Browse) with the date locked. What Smart
  // Fill does is decided from THAT date's actual current rows fetched for
  // exactly that date — never from a summary that may only cover a different
  // day (in Daily mode the page summary only holds the selected date, which
  // made a planned day look empty). An actually empty day keeps the
  // launcher's blank-day Smart Fill; a day with ANY existing row routes Smart
  // Fill to the normal Regenerate Day flow (Keep any meals? -> Review ->
  // Save), so its pending meals are never silently replaced by the empty-day
  // generator. A failed lookup opens nothing (never assumed empty).
  function openFixDay(dateStr) {
    var token = fixRouteRef.current.token + 1;
    fixRouteRef.current = { token: token, pending: true };
    setPlanLocation({ view: 'daily', date: dateStr });
    planningAPI.getSummary([dateStr])
      .then(function(res) {
        if (fixRouteRef.current.token !== token) return;
        fixRouteRef.current.pending = false;
        var data = (res && res.data) || {};
        var dayRows = (data.meals_by_date && data.meals_by_date[dateStr]) || [];
        setPlanLauncherPresetSummary(dayRows.length === 0 ? null : data);
        setPlanLauncherPresetDate(dateStr);
        setShowPlanLauncher(true);
      })
      .catch(function(err) {
        if (fixRouteRef.current.token !== token) return;
        fixRouteRef.current.pending = false;
        console.error('[PlanningHub] Fix Days day lookup failed:', err);
        toast.error("Couldn't load this day. Please try again.");
      });
  }

  // The ONLY way the Fix Days queue moves forward: an explicit tap on the
  // banner's Next problem day (more days queued) or Finish (last day).
  function advanceFixMode() {
    if (fixRouteRef.current.pending) return;
    var nextIdx = fixModeIndex + 1;
    if (nextIdx < fixModeQueue.length) {
      setFixModeIndex(nextIdx);
      setFixModeHandled(false);
      openFixDay(fixModeQueue[nextIdx]);
      return;
    }
    // Finish: back to Weekly for the week Fix Days started from (every queued
    // day is in that week). Claim the week is on track only when the REAL
    // refreshed week satisfies the Weekly Overview's own on-track rule.
    var weekAnchor = fixModeQueue[0] || selectedDate;
    var weekDates = getWeekDates(weekOffsetFor(weekAnchor, getTodayString()));
    exitFixMode();
    setPlanLocation({ view: 'weekly', date: weekAnchor });
    planningAPI.getSummary(weekDates)
      .then(function(res) {
        if (isWeekOnTrack(res && res.data, weekDates, getTodayString())) {
          toast.success('Your week is now on track');
        } else {
          toast.info('Fix Days complete');
        }
      })
      .catch(function() { toast.info('Fix Days complete'); });
  }

  // Allow user to exit the guided flow manually (called from the banner button)
  // and on every deliberate exit (bottom tabs, Grocery/Pantry shortcuts, a
  // manual date change). Ends only the temporary workflow — saved meals are
  // never touched. Storage is cleared synchronously because leaving Plan
  // unmounts this page before the sessionStorage sync effect could run.
  function exitFixMode() {
    fixRouteRef.current = { token: fixRouteRef.current.token + 1, pending: false };
    setFixModeActive(false);
    setFixModeQueue([]);
    setFixModeIndex(0);
    setFixModeHandled(false);
    setPendingFixRegenDate(null);
    clearFixModeState();
  }

  // A user-initiated date change away from the current Fix Days target ends
  // Fix Days (reselecting the target itself does not). Called only from the
  // manual date handlers — never from setPlanLocation, which Fix Days' own
  // progression (openFixDay, advanceFixMode, the handled effect) also uses.
  function exitFixModeIfLeavingTarget(dateStr) {
    if (fixModeActive && dateStr !== fixModeQueue[fixModeIndex]) exitFixMode();
  }

  // Remove meal from plan. Linked leftover pairs (cook + next-day lunch) are
  // confirmed first, then deleted together by the backend cascade.
  function removeMeal(planId, mealName, e, leftoverRole) {
    if (e) e.stopPropagation();
    if (leftoverRole === 'cook' || leftoverRole === 'leftover') {
      setLeftoverRemoveConfirm({ planId: planId, mealName: mealName, role: leftoverRole });
      return;
    }
    doRemoveMeal(planId, mealName);
  }

  function doRemoveMeal(planId, mealName) {
    setActionLoading(planId + '-remove');
    // P8b-B1: resolve the recipe id BEFORE deletion (summary still has the row).
    var removedMealId = resolvePlanMealId(planId);

    mealPlanAPI.deleteMealPlan(planId, getTodayString())
      .then(function() {
        // Direct removal of an actively planned recipe => weak negative (one
        // family-level signal even when a linked pair cascades, since both rows
        // share the recipe).
        if (removedMealId) recordTasteSignal(removedMealId, TASTE_ACTIONS.REMOVE);
        toast.success(mealName + ' removed');
        fetchData();
      })
      .catch(function(err) {
        console.error('[PlanningHub] Remove error:', err);
        toast.error('Failed to remove meal');
      })
      .finally(function() {
        setActionLoading(null);
      });
  }

  // Clear all meals for specific dates — one atomic, group-aware, idempotent
  // backend call (bulk-clear). Linked leftover pairs and completed/logged rows
  // are handled server-side; the day/week is never left partially cleared.
  function clearMeals(datesToClear) {
    if (!datesToClear || datesToClear.length === 0) { setClearConfirm(null); return; }

    setClearing(true);
    // Stable op id per clear attempt (keyed by the exact date set): reused on
    // retry so an interrupted-but-committed clear is recognised as a replay.
    var key = datesToClear.slice().sort().join(',');
    var opId;
    if (clearOpRef.current && clearOpRef.current.key === key) {
      opId = clearOpRef.current.id;
    } else {
      opId = 'clear_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      clearOpRef.current = { key: key, id: opId };
    }

    mealPlanAPI.bulkClear(datesToClear, opId)
      .then(function(res) {
        clearOpRef.current = null;
        var n = (res && res.data && res.data.deleted_count) || 0;
        if (n === 0) {
          toast.info('No planned meals to clear');
        } else {
          toast.success(n + ' meal' + (n > 1 ? 's' : '') + ' cleared');
        }
        fetchData();
      })
      .catch(function(err) {
        console.error('[PlanningHub] Clear error:', err);
        toast.error('Failed to clear meals');
        fetchData();
      })
      .finally(function() {
        setClearing(false);
        setClearConfirm(null);
      });
  }

  // Handle clear day
  function handleClearDay() {
    // In daily mode, clear selected date. In weekly mode, clear today.
    var datesToClear = mode === 'daily' ? [selectedDate] : [getTodayString()];
    clearMeals(datesToClear);
  }

  // Handle clear week
  function handleClearWeek() {
    var datesToClear = getWeekDates(weekOffset);
    clearMeals(datesToClear);
  }

  // Skip a meal slot (mark as intentionally skipped)
  function skipMealSlot(dateStr, mealType) {
    var key = dateStr + '-' + mealType;
    var newSkipped = Object.assign({}, skippedSlots);
    newSkipped[key] = true;
    setSkippedSlots(newSkipped);
    toast.success(mealType + ' marked as skipped');
  }

  // Unskip a meal slot
  function unskipMealSlot(dateStr, mealType) {
    var key = dateStr + '-' + mealType;
    var newSkipped = Object.assign({}, skippedSlots);
    delete newSkipped[key];
    setSkippedSlots(newSkipped);
    toast.success(mealType + ' unmarked');
  }

  // Check if a slot is skipped
  function isSlotSkipped(dateStr, mealType) {
    return skippedSlots[dateStr + '-' + mealType] === true;
  }

  // Get missing meal slots for current week
  function getMissingSlots() {
    var weekDates = getWeekDates(weekOffset);
    var missing = [];
    
    for (var i = 0; i < weekDates.length; i++) {
      var dateStr = weekDates[i];
      var dayMeals = getMealsForDate(dateStr);
      
      for (var j = 0; j < mealTypeOrder.length; j++) {
        var mealType = mealTypeOrder[j];
        var hasMeals = dayMeals.some(function(m) { return m.meal_type === mealType; });
        var isSkipped = isSlotSkipped(dateStr, mealType);
        
        if (!hasMeals && !isSkipped) {
          missing.push({ date: dateStr, mealType: mealType });
        }
      }
    }
    
    return missing;
  }

  // Calculate remaining macros for a day after considering skipped slots
  function getRemainingMacrosForDay(dateStr) {
    // No fabricated 2000/150/200/70 default: without real targets there is
    // nothing meaningful to compute "remaining" against.
    var dailyGoal = summary && summary.targets_ready ? summary.daily_goal : { calories: 0, protein: 0, carbs: 0, fat: 0 };
    var dayMacros = getDateMacros(dateStr);
    
    // Count planned and skipped slots
    var dayMeals = getMealsForDate(dateStr);
    var plannedSlots = 0;
    var skippedCount = 0;
    
    for (var i = 0; i < mealTypeOrder.length; i++) {
      var mealType = mealTypeOrder[i];
      var hasMeals = dayMeals.some(function(m) { return m.meal_type === mealType; });
      var isSkipped = isSlotSkipped(dateStr, mealType);
      
      if (hasMeals) plannedSlots++;
      if (isSkipped) skippedCount++;
    }
    
    var remainingSlots = 4 - plannedSlots - skippedCount;
    
    // Calculate remaining macros needed
    var remaining = {
      calories: Math.max(0, dailyGoal.calories - dayMacros.calories),
      protein: Math.max(0, dailyGoal.protein - dayMacros.protein),
      carbs: Math.max(0, dailyGoal.carbs - dayMacros.carbs),
      fat: Math.max(0, dailyGoal.fat - dayMacros.fat)
    };
    
    // Per-slot target (approximate)
    var perSlot = {
      calories: remainingSlots > 0 ? Math.round(remaining.calories / remainingSlots) : 0,
      protein: remainingSlots > 0 ? Math.round(remaining.protein / remainingSlots) : 0,
      carbs: remainingSlots > 0 ? Math.round(remaining.carbs / remainingSlots) : 0,
      fat: remainingSlots > 0 ? Math.round(remaining.fat / remainingSlots) : 0
    };
    
    return { remaining: remaining, perSlot: perSlot, remainingSlots: remainingSlots };
  }

  // Toggle favorite (account-backed)
  function toggleFavorite(mealId, mealName, e) {
    if (e) e.stopPropagation();
    ctxToggleFavorite(mealId);
  }

  // ===========================================
  // NEW THREE-BUTTON FLOW HANDLERS
  // ===========================================
  
  // Button 1: Plan Week / Plan Day
  function handlePlanButtonClick() {
    if (mode === 'daily') {
      // Daily mode: Open the Plan Food launcher (same as original "Plan Food" button)
      // This uses the existing smart planning flow with pantry and cooking preferences
      setShowPlanLauncher(true);
    } else {
      // Weekly mode: Check subscription for weekly planning
      if (!canAccess(FEATURES.CAN_GENERATE_WEEKLY_PLAN)) {
        requireFeature(FEATURES.CAN_GENERATE_WEEKLY_PLAN);
        return;
      }
      // Show modal with "Plan This Week" or "Custom" options
      setShowPlanModal(true);
      setPlanModalOption(null);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  }
  
  // Handle "Plan This Week" option in weekly modal - navigate to Weekly Planner
  function handlePlanThisWeek() {
    setShowPlanModal(false);
    // Open the shared PlanFoodLauncher for the selected week's dates (reusing
    // the existing showPlanLauncher path). The launcher itself disables
    // Browse & Choose when multiple days are selected.
    setShowPlanLauncher(true);
  }
  
  // Handle custom date range planning - routes into the SAME modern
  // Before-We-Start/regeneration flow "Plan This Week" uses (cooking prefs,
  // pantry, filters, leftovers, draft + review), scoped to exactly the
  // inclusive range the user picked instead of the visible calendar week.
  async function handlePlanCustomRange() {
    if (!customStartDate || !customEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    var rangeDates = buildInclusiveDateRange(customStartDate, customEndDate);
    if (rangeDates.length === 0) {
      toast.error('End date must be on or after the start date');
      return;
    }
    // Approved launch behavior: Custom = an inclusive range of 1-7
    // consecutive calendar days. Longer ranges are blocked here — no
    // request is ever sent for them.
    if (rangeDates.length > 7) {
      toast.error('Custom range can be at most 7 days. Please pick a shorter range.');
      return;
    }

    // Load existing pending meals for exactly this range BEFORE committing
    // to it (or opening the regenerate modal): `summary` only ever holds the
    // currently viewed week, which the custom range may not overlap at all,
    // so the keep-selection walkthrough's correctness depends on this fetch.
    // FAIL CLOSED — a failed lookup must never be treated as "no existing
    // meals" (that would silently reproduce the exact unsafe case this
    // lookup exists to prevent), so on error nothing is committed and
    // nothing generates: the Custom modal stays open exactly as it was so
    // the user can simply retry. This also means there's no gap between
    // closing Custom and opening Before You Plan on the success path — both
    // now happen together, after the fetch has already resolved.
    var rangeMealsByDate;
    try {
      rangeMealsByDate = await fetchCustomRangeMeals(rangeDates);
    } catch (err) {
      console.error('[PlanningHub] Failed to load custom range meals:', err);
      toast.error("Couldn't load existing meals. Please try again.");
      return;
    }

    setShowPlanModal(false);
    setCustomPlanningDates(rangeDates);
    setCustomRangeMealsByDate(rangeMealsByDate);
    setMode('weekly');
    // Pass both overrides explicitly rather than relying on the state
    // updates above having landed — a state setter does not synchronously
    // update the current closure, so a read of customPlanningDates /
    // customRangeMealsByDate right here could still see their PRE-update
    // (null) values on this very first decision.
    handleRegenerateClick(rangeDates, rangeMealsByDate);
  }
  
  // Button 2: Add Meal
  function handleAddMealClick() {
    if (mode === 'daily') {
      // Daily mode: go directly to Log Food for the selected date
      navigate(buildUrl('/log-food', { date: selectedDate, return: planReturn }));
    } else {
      // Weekly mode: ask which day, then go to Log Food
      setAddMealStep(1);
      setAddMealDate(null);
      setShowAddMealModal(true);
    }
  }
  
  // Handle date selection for Add Meal (weekly mode only)
  function handleAddMealDateSelect(dateStr) {
    setShowAddMealModal(false);
    navigate(buildUrl('/log-food', { date: dateStr, return: planReturn }));
  }
  
  // Decide which AI-flow step the regenerate modal should open at, based on
  // whether the target window already has any still-plannable meals to
  // optionally keep. Completed (already-logged) meals are excluded — they're
  // already eaten, so the user can't "keep" them in a regen sense; they
  // still count against daily targets via the existing meal-plan data.
  // Used by both the main Regenerate button (whole-day / whole-week) and
  // the per-day Regenerate button on the weekly view's expanded section.
  // A day needs a weekly walkthrough STOP only if it has at least one
  // REPLACEABLE regular meal — otherwise there is nothing for the user to
  // choose (linked cook/leftover rows are always kept). Skipping the stop does
  // NOT skip the day: every date in the range is still sent to the planner,
  // which can add meals around logged/linked rows when the day has room.
  // `mealsOverride` is forwarded only for the ONE synchronous decision made
  // right after confirming a custom range (see handlePlanCustomRange); every
  // other caller omits it and gets the state-based lookup (getMealsForDate,
  // or the fetched custom-range lookup once its state has committed).
  function dayHasReplaceableRegulars(dateStr, mealsOverride) {
    var meals = getMealsForRegenDate(dateStr, mealsOverride).filter(function(p) {
      return p.status !== 'skipped' && p.status !== 'completed';
    });
    return meals.some(function(p) {
      return !(p.leftover_role === 'cook' || p.leftover_role === 'leftover');
    });
  }

  // Index of the first future day (in the active regen range — the custom
  // range when one is set, otherwise the current week) that has replaceable
  // regular meals, or -1 if none.
  function firstWeeklyReplaceableIndex(datesOverride, mealsOverride) {
    var weekDates = getRegenDates(datesOverride);
    var todayStr = getTodayString();
    var futureDates = weekDates.filter(function(d) { return d >= todayStr; });
    for (var i = 0; i < futureDates.length; i++) {
      if (dayHasReplaceableRegulars(futureDates[i], mealsOverride)) return i;
    }
    return -1;
  }

  function decideRegenerateStartStep(singleDay, datesOverride, mealsOverride) {
    var stillPlannable = function(p) {
      return p.status !== 'skipped' && p.status !== 'completed';
    };
    if (singleDay) {
      var dayMeals = getMealsForDate(singleDay).filter(stillPlannable);
      return dayMeals.length > 0 ? 'keep-selection' : 'preferences';
    }
    if (mode === 'daily') {
      var meals = getMealsForDate(selectedDate).filter(stillPlannable);
      return meals.length > 0 ? 'keep-selection' : 'preferences';
    }
    // Weekly mode: only enter the walkthrough if SOME future day has a
    // replaceable regular meal; otherwise skip straight to preferences.
    return firstWeeklyReplaceableIndex(datesOverride, mealsOverride) >= 0 ? 'keep-selection' : 'preferences';
  }

  // Button 3: Regenerate. `datesOverride`/`mealsOverride` are set ONLY by
  // handlePlanCustomRange, right after confirming a custom range — passed
  // explicitly rather than relying on the customPlanningDates/
  // customRangeMealsByDate state updates it just made having landed, since a
  // state setter does not synchronously update the current closure. Every
  // other caller (the plain Regenerate button, "Plan This Week") omits both
  // and this behaves exactly as before.
  function handleRegenerateClick(datesOverride, mealsOverride) {
    // Check subscription for weekly regeneration
    if (mode === 'weekly' && !canAccess(FEATURES.CAN_REGENERATE_UNLIMITED)) {
      requireFeature(FEATURES.CAN_REGENERATE_UNLIMITED);
      return;
    }
    // Load cooking preferences when regenerate modal opens
    profileAPI.getCookingPreferences()
      .then(function(res) {
        var data = res.data;
        setSelectedCookware(data.cookware || []);
        setSelectedCookTime(data.max_cook_time);
      })
      .catch(function(err) {
        console.error('Failed to load cooking preferences:', err);
      });
    
    // Load pantry items
    pantryAPI.getPantry()
      .then(function(response) {
        var data = response.data || {};
        setFullPantryData(data);
        var allItems = [];
        ['proteins', 'pantry', 'dairy', 'produce', 'other'].forEach(function(cat) {
          var items = data[cat] || [];
          items.forEach(function(item) {
            if (item && item.name && typeof item.name === 'string') {
              allItems.push(item.name);
            }
          });
        });
        setPantryItems(allItems);
      })
      .catch(function(err) {
        console.error('Failed to load pantry:', err);
      });
    
    // Skip the legacy 'confirm' step. Regenerate = AI regeneration; route
    // straight to keep-selection (or preferences when nothing to keep).
    setMealsToKeep([]);
    setWeeklyMealsToKeepByDate({});
    // Start the weekly walkthrough on the first day with replaceable regulars.
    var firstIdx = firstWeeklyReplaceableIndex(datesOverride, mealsOverride);
    setWeeklyWalkthroughDayIndex(firstIdx >= 0 ? firstIdx : 0);
    setRegenerateSingleDay(null);
    setRegenerateStep(decideRegenerateStartStep(null, datesOverride, mealsOverride));
    setShowRegenerateModal(true);
  }
  
  // Handle per-day regeneration from weekly view expanded section
  function handleRegenerateDayClick(dateStr) {
    // Load cooking preferences when regenerate modal opens
    profileAPI.getCookingPreferences()
      .then(function(res) {
        var data = res.data;
        setSelectedCookware(data.cookware || []);
        setSelectedCookTime(data.max_cook_time);
      })
      .catch(function(err) {
        console.error('Failed to load cooking preferences:', err);
      });
    
    // Load pantry items
    pantryAPI.getPantry()
      .then(function(response) {
        var data = response.data || {};
        setFullPantryData(data);
        var allItems = [];
        ['proteins', 'pantry', 'dairy', 'produce', 'other'].forEach(function(cat) {
          var items = data[cat] || [];
          items.forEach(function(item) {
            if (item && item.name && typeof item.name === 'string') {
              allItems.push(item.name);
            }
          });
        });
        setPantryItems(allItems);
      })
      .catch(function(err) {
        console.error('Failed to load pantry:', err);
      });
    
    setMealsToKeep([]);
    setRegenerateSingleDay(dateStr);
    setRegenerateStep(decideRegenerateStartStep(dateStr));
    setShowRegenerateModal(true);
  }
  
  // Close regenerate modal and reset state
  function handleCloseRegenerateModal() {
    setShowRegenerateModal(false);
    // Reset to the new initial step. The open handlers always re-decide
    // based on context, so this is just a safe default.
    setRegenerateStep('keep-selection');
    setMealsToKeep([]);
    setRegenerateSingleDay(null);
    setLeftoversTouched(false);  // new Before You Plan session re-derives the default
    setWeeklyMealsToKeepByDate({});
    setWeeklyWalkthroughDayIndex(0);
    setShowFiltersPanel(false);
    setSelectedTags([]);
    setSelectedCuisine([]);
    // CUSTOM RANGE: clear both overrides on every close (generated,
    // cancelled, or backgrounded) so neither can leak into a later normal
    // Plan This Week / Regenerate operation.
    setCustomPlanningDates(null);
    setCustomRangeMealsByDate(null);
  }
  
  // Toggle a meal in the "keep" selection (daily mode)
  function toggleMealToKeep(planId) {
    if (mealsToKeep.includes(planId)) {
      setMealsToKeep(mealsToKeep.filter(function(id) { return id !== planId; }));
    } else {
      setMealsToKeep(mealsToKeep.concat([planId]));
    }
  }
  
  // Toggle a meal in weekly walkthrough
  function toggleWeeklyMealToKeep(dateStr, planId) {
    var current = weeklyMealsToKeepByDate[dateStr] || [];
    if (current.includes(planId)) {
      var newList = current.filter(function(id) { return id !== planId; });
      setWeeklyMealsToKeepByDate(Object.assign({}, weeklyMealsToKeepByDate, { [dateStr]: newList }));
    } else {
      setWeeklyMealsToKeepByDate(Object.assign({}, weeklyMealsToKeepByDate, { [dateStr]: current.concat([planId]) }));
    }
  }
  
  // Proceed from keep-selection to preferences (daily mode)
  function handleProceedFromKeepSelection() {
    setRegenerateStep('preferences');
  }
  
  // Handle weekly walkthrough - go to next day or preferences
  function handleWeeklyWalkthroughNext() {
    var weekDates = getRegenDates();
    var todayStr = getTodayString();
    
    // Filter to only include today and future dates
    var futureDates = weekDates.filter(function(d) { return d >= todayStr; });
    
    var nextIndex = weeklyWalkthroughDayIndex + 1;
    
    // Find next day that has REPLACEABLE regular meals (only future dates).
    // Days with only linked cook/leftover rows have no keep choice to show, so
    // their stop is skipped; they are still regenerated (planned around).
    while (nextIndex < futureDates.length) {
      if (dayHasReplaceableRegulars(futureDates[nextIndex])) {
        setWeeklyWalkthroughDayIndex(nextIndex);
        return;
      }
      nextIndex++;
    }
    
    // No more days with meals, go to preferences
    // Flatten weeklyMealsToKeepByDate into a single array
    var allKeptPlanIds = [];
    Object.values(weeklyMealsToKeepByDate).forEach(function(ids) {
      allKeptPlanIds = allKeptPlanIds.concat(ids);
    });
    setMealsToKeep(allKeptPlanIds);
    setRegenerateStep('preferences');
  }
  
  // Handle weekly walkthrough - skip remaining days
  function handleWeeklyWalkthroughSkip() {
    // Flatten weeklyMealsToKeepByDate into a single array
    var allKeptPlanIds = [];
    Object.values(weeklyMealsToKeepByDate).forEach(function(ids) {
      allKeptPlanIds = allKeptPlanIds.concat(ids);
    });
    setMealsToKeep(allKeptPlanIds);
    setRegenerateStep('preferences');
  }
  
  // Edit cooking preferences
  function handleEditCookingPrefs() {
    setRegenerateStep('cooking');
  }
  
  // Edit pantry
  function handleEditPantry() {
    setRegenerateStep('pantry');
  }
  
  // Save cooking preferences
  function handleSaveCookingPrefs(cookware, cookTime) {
    setSelectedCookware(cookware);
    setSelectedCookTime(cookTime);
    profileAPI.updateCookingPreferences({ cookware: cookware, max_cook_time: cookTime })
      .catch(function(err) {
        console.error('Failed to save cooking preferences:', err);
      });
    setRegenerateStep('preferences');
  }
  
  // Save pantry items - PantrySelector handles backend save on Done
  function handleSavePantry(items) {
    setPantryItems(items);
    setRegenerateStep('preferences');
  }
  
  // Execute regeneration with kept meals
  async function handleProceedToGenerate() {
    setRegenerateStep('generating');
    setRegenerating(true);
    
    var today = getTodayString();
    
    // Determine dates to regenerate based on mode
    var allWeekDates;
    if (regenerateSingleDay) {
      // Single day regeneration from weekly view - use the date directly
      // Don't filter past dates for single day - user explicitly chose this day
      allWeekDates = [regenerateSingleDay];
    } else if (mode === 'daily') {
      allWeekDates = [selectedDate];
    } else {
      allWeekDates = getRegenDates();
    }
    
    // Filter out past dates (keep today and future dates only)
    // Skip filtering for single day regeneration - user explicitly selected that day
    var datesToRegenerate;
    if (regenerateSingleDay) {
      datesToRegenerate = allWeekDates; // Use as-is for single day
    } else {
      datesToRegenerate = allWeekDates.filter(function(dateStr) {
        return dateStr >= today;
      });
    }
    
    // If no valid dates to regenerate, show message and return
    if (datesToRegenerate.length === 0) {
      toast.info('No future dates to regenerate');
      handleCloseRegenerateModal();
      setRegenerating(false);
      return;
    }
    
    try {
      var preferences = {
        fewer_ingredients: false,
        quick_prep: selectedCookTime && selectedCookTime <= 20,
        exclude_types: []
      };
      
      var cookingPreferences = {
        cookware: selectedCookware,
        max_cook_time: selectedCookTime
      };
      
      // Build filters object
      var filters = null;
      if (selectedTags.length > 0 || selectedCuisine.length > 0) {
        filters = {};
        if (selectedTags.length > 0) filters.tags = selectedTags;
        if (selectedCuisine.length > 0) filters.cuisine = selectedCuisine;
      }
      
      // Pass meals to keep (or null if none selected)
      // LEFTOVERS: linked leftover pairs (cook + next-day lunch) are ALWAYS
      // kept implicitly during regeneration — never regenerated, never
      // excluded. Shared helper merges their plan_ids into the kept set so the
      // pair is never orphaned or duplicated, regardless of manual selection.
      var _regenDayMeals = [];
      for (var li = 0; li < datesToRegenerate.length; li++) {
        _regenDayMeals = _regenDayMeals.concat(getMealsForRegenDate(datesToRegenerate[li]) || []);
      }
      var _sel = buildRegenSelection(_regenDayMeals, mealsToKeep);
      var mealsToKeepParam = _sel.mealsToKeep;
      var excludeMealIds = _sel.excludeMealIds;
      
      console.log('[Regenerate] Calling API with:', {
        dates: datesToRegenerate,
        mealsToKeep: mealsToKeepParam,
        excludeMealIds: excludeMealIds,
        filters: filters
      });
      
      // LEFTOVERS-LITE: only for multi-day (weekly) generation with >=2 dates.
      var leftoversForRequest = includeLeftovers && !regenerateSingleDay && mode !== 'daily' && datesToRegenerate.length >= 2;

      var res = await smartFillAPI.auto(datesToRegenerate, preferences, [], cookingPreferences, filters, mealsToKeepParam, excludeMealIds, true, leftoversForRequest);
      // P8b-B1: regeneration request succeeded — teach taste from the FINAL
      // selection (manual keeps +keep, regenerated-away regulars -regenerate,
      // linked structural keeps neutral). Draft vs commit is irrelevant here:
      // the user's keep/replace intent is finalized once this request succeeds.
      if (res && res.data && res.data.success) {
        recordRegenTasteSignals(_regenDayMeals, _sel);
      }
      
      console.log('[Regenerate] Draft response:', {
        success: res.data.success,
        draft: res.data.draft,
        meals_added: res.data.meals_added,
        new_meals_count: res.data.new_meals_count,
        has_changes: res.data.has_changes,
      });

      // `success && has_changes` alone is NOT proof a complete replacement
      // plan exists for every requested date: has_changes is true whenever
      // ANY non-kept pending row would be removed on confirm, independent
      // of whether the planner actually generated a full replacement (see
      // server.py smart_fill_auto's has_changes computation). A day can be
      // success:true, has_changes:true, AND listed in incomplete_dates at
      // the same time — e.g. a day with two protected linked rows and no
      // feasible replacement for the rest. classifyAutoRegenResult is the
      // single place that resolves this instead of every call site
      // re-deriving its own meaning for these fields.
      var classification = classifyAutoRegenResult(res.data, datesToRegenerate);

      if (classification.outcome === 'reviewable') {
        handleCloseRegenerateModal();
        // Navigate to review screen with draft data. Save / Cancel return to
        // exactly this Plan location (returnTo), not to Weekly/current week.
        navigate('/plan/review', { state: startFlowState({
          returnTo: planReturn,
          added_plans: res.data.added_plans,
          daily_target: res.data.daily_target || res.data.summary,
          daily_results: res.data.daily_results,
          replace_plan_ids: res.data.replace_plan_ids || [],
          dates: datesToRegenerate,
          // res.data.goal is always the user's real goal here: this is a
          // draft-review call (draft=true) into the fail-closed
          // /smart-fill/auto, which never succeeds without one — no
          // "Maintain" fallback (see AGENTS.md pre-launch audit
          // nutrition-readiness follow-up).
          goal: res.data.goal,
          weekOffset: weekOffset,
        }) });
      } else if (classification.outcome === 'no_valid_plan') {
        // At least one requested date has no complete replacement. Never
        // navigate to Review with only protected/kept rows presented as if
        // regeneration succeeded, and never apply anything from this
        // response — the existing saved/draft day (this component's own
        // `summary`/`plans` state, untouched by this branch) is exactly
        // what the user still sees. Reuses the same reason -> copy mapping
        // PlanReview's own regenerate-day failure uses.
        toast.error(friendlyRegenerateReasonText(
          firstIncompleteReason(classification),
          "Couldn't find a different valid plan for the selected day(s). Try removing a filter or excluded meal, then regenerate again."
        ));
        handleCloseRegenerateModal();
      } else if (classification.outcome === 'no_changes') {
        // Zero new meals AND nothing to remove — the day already fits. Close
        // with a short, non-error informational message; make NO save request.
        toast.info(retainedOnlyMessage(res.data.daily_results));
        handleCloseRegenerateModal();
      } else {
        // classification.outcome === 'failed'
        toast.info('No meals generated');
        handleCloseRegenerateModal();
      }
    } catch (err) {
      console.error('[Regenerate] Error:', err);
      // 402s (subscription_required / daily_limit_reached /
      // planning_around_logged_meals_requires_pro) are already surfaced by
      // the global axios interceptor — opening the Paywall or the
      // ProUpgradeNotice modal. Showing an extra "Failed to generate plan"
      // toast on top of that is what users perceived as a glitchy failed
      // state. Close the modal quietly and let the global handler own UX.
      var status = err?.response?.status;
      var code = err?.response?.data?.detail?.error || err?.response?.data?.error;
      if (
        status === 402 &&
        (code === 'subscription_required' ||
          code === 'daily_limit_reached' ||
          code === 'planning_around_logged_meals_requires_pro')
      ) {
        handleCloseRegenerateModal();
        return;
      }
      toast.error('Failed to generate plan');
      setRegenerateStep('preferences');
    } finally {
      setRegenerating(false);
    }
  }
  
  // Legacy: Direct confirm regeneration (for quick regeneration without keeping meals)
  function handleConfirmRegenerate() {
    setShowRegenerateModal(false);
    setPlanningInProgress(true);
    
    var datesToRegenerate = mode === 'daily' ? [selectedDate] : getWeekDates(weekOffset);

    // Atomic, group-aware bulk-clear (never partial), then generate fresh meals.
    var opId = 'regen_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    mealPlanAPI.bulkClear(datesToRegenerate, opId)
      .then(function() {
        // Call smart fill auto with replace_existing: true (backend default)
        // The backend will regenerate fresh meals for these dates
        return smartFillAPI.auto(datesToRegenerate, {});
      })
      .then(function(res) {
        toast.success(mode === 'daily' ? 'Day regenerated!' : 'Week regenerated!');
        // Guided Fix Days: signal auto-advance on the next effect tick, but
        // only for the date(s) this action actually regenerated — the
        // consumer verifies the current queue target is among them before
        // advancing (see AGENTS.md pre-launch audit, finding FIXDAYS-1).
        if (fixModeActive) {
          setFixModeAdvancePending(datesToRegenerate);
        }
        fetchData();
      })
      .catch(function(err) {
        console.error('Failed to regenerate:', err);
        // 402 paywall path is already handled globally; suppress duplicate
        // "Failed to regenerate meals" toast that overlapped the paywall
        // and produced the glitchy failed-state flicker.
        var status = err?.response?.status;
        var code = err?.response?.data?.detail?.error || err?.response?.data?.error;
        if (
          status === 402 &&
          (code === 'subscription_required' ||
            code === 'daily_limit_reached' ||
            code === 'planning_around_logged_meals_requires_pro')
        ) {
          fetchData();
          return;
        }
        toast.error('Failed to regenerate meals');
        fetchData(); // Refresh to show current state
      })
      .finally(function() {
        setPlanningInProgress(false);
      });
  }
  
  // Swap meal - navigate to browse meals to replace
  function swapMeal(date, mealType, planId, e, leftoverGroupId, leftoverRole) {
    if (e) e.stopPropagation();
    // LEFTOVERS: a linked row never does the generic one-row delete+add swap.
    if (leftoverGroupId) {
      // Inspect the pair from the current summary to find the parent cook.
      var rows = [];
      if (summary && summary.meals_by_date) {
        Object.keys(summary.meals_by_date).forEach(function(d) {
          (summary.meals_by_date[d] || []).forEach(function(m) {
            if (m.leftover_group_id === leftoverGroupId) rows.push(Object.assign({}, m, { date: d }));
          });
        });
      }
      var clicked = rows.find(function(m) { return m.plan_id === planId; });
      var cook = rows.find(function(m) { return m.leftover_role === 'cook'; });
      var todayStr = getTodayString();
      var clickedIsLeftover = clicked ? clicked.leftover_role === 'leftover' : (leftoverRole === 'leftover');
      var parentFinalized;
      if (cook) {
        parentFinalized = (['completed', 'skipped', 'planned'].indexOf(cook.status) !== -1) || cook.date < todayStr;
      } else if (clickedIsLeftover) {
        // Partner cook is the previous day (validator: leftover == cook + 1).
        var cd = new Date(date + 'T12:00:00'); cd.setDate(cd.getDate() - 1);
        parentFinalized = cd.toISOString().slice(0, 10) < todayStr;
      } else {
        parentFinalized = false;
      }
      if (clickedIsLeftover && parentFinalized) {
        // Parent can't safely change -> normal single-meal Lunch swap browser,
        // finished by the atomic child-only replacement endpoint.
        var childMealId = clicked ? (clicked.meal_id || '') : '';
        navigate(buildUrl('/meals', {
          guided: 'true', date: date, type: String(mealType).toLowerCase(),
          return: planReturn, swap: 'true', replacePlanId: planId,
          linkedChild: '1', expectedMealId: childMealId,
        }), { state: startFlowState() });
        return;
      }
      // Future pending pair -> pair-safe Replace Both drawer.
      openPairSwap(planId, date, leftoverGroupId);
      return;
    }
    // Ordinary Swap: the browser returns to exactly this Plan location, and a
    // completed Swap pops back here instead of staying behind Back.
    navigate(buildUrl('/meals', {
      guided: 'true', date: date, type: mealType, return: planReturn,
      swap: 'true', replacePlanId: planId, outgoingMealId: resolvePlanMealId(planId),
    }), { state: startFlowState() });
  }

  // LEFTOVERS pair-safe swap: collect the group's rows from the current summary
  // and hand off to the shared PairSwapDialog (which loads candidates + calls
  // the pair-safe swapPair route). Cancel writes nothing.
  function openPairSwap(planId, date, groupId) {
    var pairRows = [];
    if (summary && summary.meals_by_date) {
      Object.keys(summary.meals_by_date).forEach(function(d) {
        (summary.meals_by_date[d] || []).forEach(function(m) {
          if (m.leftover_group_id === groupId) pairRows.push(Object.assign({}, m, { date: d }));
        });
      });
    }
    setPairSwapContext({ groupId: groupId, planId: planId, date: date, pairRows: pairRows });
  }

  // Format date for display
  function formatDate(dateStr) {
    // Use noon to avoid timezone issues with date-only strings
    var d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function formatShortDate(dateStr) {
    // Use noon to avoid timezone issues with date-only strings
    var d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
  }

  // Check if date is today
  function isToday(dateStr) {
    return dateStr === getTodayString();
  }

  // Week navigation (moves the anchor a whole week, so Daily keeps the weekday)
  function prevWeek() {
    setPlanLocation({ view: 'weekly', date: addDays(selectedDate, -7) });
  }

  function nextWeek() {
    setPlanLocation({ view: 'weekly', date: addDays(selectedDate, 7) });
  }

  // Daily navigation
  function prevDay() {
    exitFixModeIfLeavingTarget(addDays(selectedDate, -1));
    setPlanLocation({ view: 'daily', date: addDays(selectedDate, -1) });
  }

  function nextDay() {
    exitFixModeIfLeavingTarget(addDays(selectedDate, 1));
    setPlanLocation({ view: 'daily', date: addDays(selectedDate, 1) });
  }

  // Toggle meal type expansion
  function toggleMealType(key) {
    var newExpanded = Object.assign({}, expandedMealTypes);
    newExpanded[key] = !newExpanded[key];
    setExpandedMealTypes(newExpanded);
  }

  // Toggle day expansion in weekly view
  function toggleDay(dateStr) {
    var newExpanded = Object.assign({}, expandedDays);
    newExpanded[dateStr] = !newExpanded[dateStr];
    setExpandedDays(newExpanded);
  }

  // Toggle grocery item
  function toggleGroceryItem(itemName) {
    var newChecked = Object.assign({}, checkedItems);
    if (newChecked[itemName]) {
      delete newChecked[itemName];
    } else {
      newChecked[itemName] = true;
    }
    setCheckedItems(newChecked);
  }

  // Calculate macro totals for a meal type
  function getMealTypeMacros(meals, mealType) {
    var filtered = meals.filter(function(m) { return m.meal_type === mealType; });
    // Skipped/replaced rows never contribute to nutrition (matches Swap /
    // Progress and the backend's ACTIVE_DAY_STATUSES).
    var totals = sumDayMacros(filtered);
    totals.count = filtered.length;
    return totals;
  }

  // Get all meals for a specific date
  function getMealsForDate(dateStr) {
    if (!summary || !summary.meals_by_date) return [];
    return summary.meals_by_date[dateStr] || [];
  }

  // CUSTOM RANGE: the meals lookup every regen/keep-selection decision point
  // should use. Prefers an explicit `mealsOverride` (passed only for the ONE
  // synchronous decision made right after confirming a custom range, before
  // its state updates have committed), else the fetched custom-range lookup
  // when a custom range is active, else getMealsForDate() unchanged. Never
  // used by normal visible-week rendering.
  function getMealsForRegenDate(dateStr, mealsOverride) {
    var source = mealsOverride !== undefined
      ? mealsOverride
      : (customPlanningDates ? customRangeMealsByDate : null);
    if (source) return source[dateStr] || [];
    return getMealsForDate(dateStr);
  }

  // Fetch the planning summary for exactly `dates` and return its
  // meals-by-date map. Used ONLY to populate customRangeMealsByDate — never
  // writes to `summary` itself, so the visible-week page state (and anything
  // that watches it, e.g. Guided Fix Plan auto-advance) is untouched.
  // INTENTIONALLY fails CLOSED: any error here (network, 5xx, etc.) is left
  // to propagate uncaught. The keep-selection walkthrough's correctness
  // depends on this data — treating a failed lookup as "no existing meals"
  // would silently reproduce the exact unsafe case this lookup exists to
  // prevent. The caller (handlePlanCustomRange) is responsible for catching
  // this and refusing to proceed.
  async function fetchCustomRangeMeals(dates) {
    var res = await planningAPI.getSummary(dates);
    return (res && res.data && res.data.meals_by_date) || {};
  }

  // P8b-B1: resolve a plan_id to its catalog meal_id from the loaded summary
  // (never infer from title). Used to signal weak taste on a direct remove/swap.
  function resolvePlanMealId(planId) {
    if (!planId || !summary || !summary.meals_by_date) return null;
    var found = null;
    Object.keys(summary.meals_by_date).forEach(function (d) {
      (summary.meals_by_date[d] || []).forEach(function (m) {
        if (m.plan_id === planId) found = m.meal_id || null;
      });
    });
    return found;
  }

  // Calculate macro totals for a date
  function getDateMacros(dateStr) {
    // Excludes skipped/replaced rows so a skipped meal never inflates the
    // displayed day total (previously it did — Swap/Progress already excluded).
    return sumDayMacros(getMealsForDate(dateStr));
  }

  // Get week range text - uses shared utility for consistency
  function getWeekRangeTextLocal() {
    var dates = getWeekDates(weekOffset);
    return getWeekRangeText(dates);
  }

  // Navigate to guided planning for a specific date (full day planning)
  function goToGuidedPlanning(targetDate) {
    var dateToUse = targetDate || (mode === 'daily' ? selectedDate : getTodayString());
    navigate(buildUrl('/plan/guided', { date: dateToUse, return: planReturn }), { state: startFlowState() });
  }

  // Navigate to browse meals for adding a single meal (not full day planning)
  function goToAddMeal(targetDate, mealType) {
    var dateToUse = targetDate || (mode === 'daily' ? selectedDate : getTodayString());
    // Use simple browse mode (no guided=true) so back button returns directly to Plan
    navigate(buildUrl('/meals', { date: dateToUse, type: mealType, return: planReturn }), { state: startFlowState() });
  }

  // Navigate to meal view
  function goToMealView(mealId, mealType, planId, planItem) {
    var params = new URLSearchParams();
    params.set('return', planReturn);
    // Pass the row's real date so the detail screen's already-logged decision
    // is scoped to the day this saved row actually belongs to.
    params.set('date', planRowDate(planItem, selectedDate));
    if (mealType) params.set('type', mealType);
    if (planId) params.set('planId', planId);
    if (planItem) {
      if (planItem.status) params.set('status', planItem.status);
      // Pass macros so MealView has them even for custom meals
      if (planItem.macros) {
        params.set('calories', planItem.macros.calories || 0);
        params.set('protein', planItem.macros.protein || 0);
        params.set('carbs', planItem.macros.carbs || 0);
        params.set('fat', planItem.macros.fat || 0);
      }
      // Pass scale factor for scaled meals
      if (planItem.scale_factor && planItem.scale_factor !== 1.0) {
        params.set('scaleFactor', planItem.scale_factor);
        if (planItem.scale_label) params.set('scaleLabel', planItem.scale_label);
      }
      // LEFTOVERS: pass linkage so MealView can show batch-prep vs reheat.
      if (planItem.leftover_role) params.set('leftoverRole', planItem.leftover_role);
      if (planItem.prep_scale_factor) params.set('prepScaleFactor', planItem.prep_scale_factor);
    }
    navigate('/meal-view/' + mealId + '?' + params.toString(), { state: startFlowState({ mealType: mealType }) });
  }

  // Navigate to grocery page
  function goToGrocery() {
    if (fixModeActive) exitFixMode();
    navigate('/grocery');
  }

  // MEAL TYPE ORDER
  var mealTypeOrder = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

  // Render expandable meal group
  function renderMealGroup(meals, mealType, keyPrefix) {
    var key = keyPrefix + '-' + mealType;
    var isExpanded = expandedMealTypes[key];
    var typeMeals = meals.filter(function(m) { return m.meal_type === mealType; });
    var macros = getMealTypeMacros(meals, mealType);
    var slotSkipped = isSlotSkipped(keyPrefix, mealType);
    
    return (
      <div key={key} className={'border rounded-lg overflow-hidden ' + (slotSkipped ? 'border-muted bg-muted/10' : 'border-border/30')} data-testid={'meal-group-' + mealType.toLowerCase()}>
        {/* Collapsed header */}
        <button
          onClick={function() { toggleMealType(key); }}
          className={'w-full flex items-center justify-between p-3 transition-colors ' + (slotSkipped ? 'bg-muted/20 hover:bg-muted/30' : 'bg-secondary/20 hover:bg-secondary/30')}
          data-testid={'toggle-' + mealType.toLowerCase()}
        >
          <div className="flex items-center gap-3">
            <span className={'font-medium text-sm ' + (slotSkipped ? 'text-muted-foreground line-through' : '')}>{mealType}</span>
            {slotSkipped ? (
              <span className="text-xs text-muted-foreground italic">(skipped)</span>
            ) : (
              <span className="text-xs text-muted-foreground">({typeMeals.length} meal{typeMeals.length !== 1 ? 's' : ''})</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!slotSkipped && (
              <span className="text-xs text-muted-foreground">
                {Math.round(macros.calories)} cal • {Math.round(macros.protein)}g P
              </span>
            )}
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>
        
        {/* Expanded content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-2 space-y-2 bg-background">
                {/* Skipped state */}
                {slotSkipped ? (
                  <div className="text-center py-3">
                    <p className="text-sm text-muted-foreground mb-2">This meal slot is skipped</p>
                    <Button
                      onClick={function(e) { e.stopPropagation(); unskipMealSlot(keyPrefix, mealType); }}
                      size="sm"
                      variant="outline"
                      className="text-xs"
                    >
                      Unskip {mealType}
                    </Button>
                  </div>
                ) : (
                  <>
                    {typeMeals.length > 0 ? typeMeals.map(function(meal) {
                      var isFavorite = favHas(meal.meal_id);
                      var isRemoving = actionLoading === meal.plan_id + '-remove';
                      var isSwapping = actionLoading === meal.plan_id + '-swap';
                      
                      return (
                        <MealCard
                          key={meal.plan_id}
                          meal={{
                            meal_id: meal.meal_id,
                            name: meal.meal_name,
                            calories: (meal.macros && meal.macros.calories) || 0,
                            protein: (meal.macros && meal.macros.protein) || 0,
                            carbs: (meal.macros && meal.macros.carbs) || 0,
                            fat: (meal.macros && meal.macros.fat) || 0,
                            image_url: meal.image_url
                          }}
                          mealType={meal.meal_type}
                          planId={meal.plan_id}
                          status={meal.status}
                          isFavorite={isFavorite}
                          variant="plan"
                          showLoggedBadge={meal.status === 'completed'}
                          leftoverRole={meal.leftover_role}
                          onCardClick={function() { goToMealView(meal.meal_id, meal.meal_type, meal.plan_id, meal); }}
                          onSwap={function(e) { 
                            e.stopPropagation();
                            swapMeal(keyPrefix, mealType, meal.plan_id, e, meal.leftover_group_id, meal.leftover_role);
                          }}
                          onRemove={function(e) { 
                            e.stopPropagation();
                            removeMeal(meal.plan_id, meal.meal_name, e, meal.leftover_role);
                          }}
                          onFavorite={function(e) { toggleFavorite(meal.meal_id, meal.meal_name, e); }}
                          isLoading={{
                            swap: isSwapping,
                            remove: isRemoving,
                            favorite: false
                          }}
                        />
                      );
                    }) : (
                      <p className="text-sm text-muted-foreground text-center py-2">No {mealType.toLowerCase()} planned</p>
                    )}
                    
                    {/* Action buttons row */}
                    <div className="flex gap-2">
                      {/* Add button - allows multiple meals per section */}
                      <Button
                        onClick={function(e) { e.stopPropagation(); goToAddMeal(keyPrefix, mealType); }}
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-lg text-xs border-dashed"
                        data-testid={'add-' + mealType.toLowerCase() + '-btn'}
                      >
                        <Plus size={14} className="mr-1" />
                        Add {mealType}
                      </Button>
                      
                      {/* Skip button - only show if no meals planned */}
                      {typeMeals.length === 0 && (
                        <Button
                          onClick={function(e) { e.stopPropagation(); skipMealSlot(keyPrefix, mealType); }}
                          size="sm"
                          variant="ghost"
                          className="rounded-lg text-xs text-muted-foreground hover:text-foreground"
                          data-testid={'skip-' + mealType.toLowerCase() + '-btn'}
                        >
                          <Ban size={14} className="mr-1" />
                          Skip
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Targets aren't ready to show. A response without `targets_ready` (an older
  // planning-summary contract) is a load problem, never "set up your targets";
  // `targets_ready: false` lets the shared nutrition readiness say why.
  function renderTargetsPrompt() {
    return (
      <NutritionTargetsSetupPrompt
        compact
        contractUnknown={!summary || typeof summary.targets_ready !== 'boolean'}
        onRefresh={fetchData}
      />
    );
  }

  // Render Planned vs Goal macro summary for Daily view (no wrapper - embedded in status header)
  function renderDailyMacroSummary() {
    if (!summary) return null;
    // Nutrition setup incomplete: never render "/2000"-style fake targets or
    // classify against them — show the same setup CTA used elsewhere
    // (see AGENTS.md pre-launch audit nutrition-readiness follow-up).
    if (summary.targets_ready !== true) {
      return renderTargetsPrompt();
    }

    var goal = summary.daily_goal;
    var dayMacros = getDateMacros(selectedDate);

    var macroItems = [
      { label: 'Cal', planned: Math.round(dayMacros.calories), goal: goal.calories, macro: 'calories' },
      { label: 'P', planned: Math.round(dayMacros.protein), goal: goal.protein, macro: 'protein' },
      { label: 'C', planned: Math.round(dayMacros.carbs), goal: goal.carbs, macro: 'carbs' },
      { label: 'F', planned: Math.round(dayMacros.fat), goal: goal.fat, macro: 'fat' }
    ];
    var summaryGoal = summary.goal;

    return (
      <div data-testid="daily-macro-summary">
        <div className="grid grid-cols-4 gap-2">
          {macroItems.map(function(item) {
            var pct = item.goal > 0 ? Math.round((item.planned / item.goal) * 100) : 0;
            var barWidth = Math.min(pct, 100);
            
            return (
              <div key={item.label} className="text-center">
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden mb-1">
                  <div 
                    className={'h-full rounded-full transition-all ' + getMacroBarColor(pct, item.macro, summaryGoal)}
                    style={{ width: barWidth + '%' }}
                  />
                </div>
                <p className="text-xs font-mono">
                  <span>{item.planned}</span>
                  <span className="text-muted-foreground">/{item.goal}</span>
                </p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Render Daily View - flat meal list (like Home screen)
  function renderDailyView() {
    var meals = getMealsForDate(selectedDate);
    
    // Sort meals by meal type order: Breakfast → Lunch → Dinner → Snack
    var sortedMeals = [];
    for (var i = 0; i < mealTypeOrder.length; i++) {
      var mealType = mealTypeOrder[i];
      var typeMeals = meals.filter(function(m) { return m.meal_type === mealType; });
      sortedMeals = sortedMeals.concat(typeMeals);
    }
    
    // Calculate status based on macros using shared goal-aware logic. Without
    // real, complete targets we cannot classify the day at all — never
    // fabricate a target or assume "Maintain" (see AGENTS.md pre-launch
    // audit nutrition-readiness follow-up).
    var targetsReady = !!(summary && summary.targets_ready);
    var dayMacros = getDateMacros(selectedDate);
    var dayStatus = (targetsReady && sortedMeals.length > 0)
      ? getMacroStatus(dayMacros, summary.daily_goal, summary.goal)
      : 'none';
    var dayStatusDisplay = dayStatus !== 'none' ? getStatusDisplay(dayStatus) : null;
    var statusText = dayStatusDisplay
      ? dayStatusDisplay.label
      : (sortedMeals.length === 0 ? 'Empty' : (targetsReady ? 'Incomplete' : 'Set up targets to see status'));
    var statusColor = dayStatusDisplay ? dayStatusDisplay.textColor : 'text-muted-foreground';
    var isComplete = dayStatus === 'on-track';
    var isLastFixDay = fixModeIndex >= fixModeQueue.length - 1;

    return (
      <div className="space-y-3">
        {/* Guided Fix Mode banner — visible while the user is walking through
            problem days. The queue moves ONLY through the explicit Next
            problem day / Finish action (emphasized once this day is handled). */}
        {fixModeActive && fixModeQueue.length > 0 && (
          <div className="bg-accent/10 border border-accent/30 rounded-xl p-3" data-testid="fix-mode-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Wand2 size={14} className="text-accent shrink-0" />
                <span className="text-[12px] font-medium text-accent" data-testid="fix-mode-progress">
                  {fixModeHandled
                    ? 'Day ' + (fixModeIndex + 1) + ' of ' + fixModeQueue.length + ' updated'
                    : 'Fixing day ' + (fixModeIndex + 1) + ' of ' + fixModeQueue.length}
                </span>
              </div>
              <button
                onClick={function() { exitFixMode(); }}
                className="text-[12px] font-medium text-muted-foreground hover:text-foreground"
                data-testid="fix-mode-exit"
              >
                Exit
              </button>
            </div>
            <Button
              onClick={function() { advanceFixMode(); }}
              variant={fixModeHandled ? 'default' : 'outline'}
              className={'w-full h-9 mt-2 rounded-full text-sm' + (fixModeHandled ? ' bg-accent text-white hover:bg-accent/90' : '')}
              data-testid={isLastFixDay ? 'fix-mode-finish' : 'fix-mode-next'}
            >
              {isLastFixDay ? 'Finish' : 'Next problem day'}
            </Button>
          </div>
        )}

        {/* Status Header - only show macro completion status */}
        <div className="bg-card border border-border/40 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className={'text-sm font-semibold ' + statusColor}>{statusText}</p>
            {isComplete && (
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                <Check size={16} className="text-accent" />
              </div>
            )}
          </div>
          
          {/* Macro Summary */}
          {renderDailyMacroSummary()}
        </div>
        
        {/* Flat Meal List */}
        {sortedMeals.length > 0 ? (
          <div className="space-y-2">
            {sortedMeals.map(function(meal) {
              var isFavorite = favHas(meal.meal_id);
              var isRemoving = actionLoading === meal.plan_id + '-remove';
              var isSwapping = actionLoading === meal.plan_id + '-swap';
              
              return (
                <MealCard
                  key={meal.plan_id}
                  meal={{
                    meal_id: meal.meal_id,
                    name: meal.meal_name,
                    calories: (meal.macros && meal.macros.calories) || 0,
                    protein: (meal.macros && meal.macros.protein) || 0,
                    carbs: (meal.macros && meal.macros.carbs) || 0,
                    fat: (meal.macros && meal.macros.fat) || 0,
                    image_url: meal.image_url
                  }}
                  mealType={meal.meal_type}
                  planId={meal.plan_id}
                  status={meal.status}
                  isFavorite={isFavorite}
                  variant="plan"
                  showLoggedBadge={meal.status === 'completed'}
                  leftoverRole={meal.leftover_role}
                  onCardClick={function() { goToMealView(meal.meal_id, meal.meal_type, meal.plan_id, meal); }}
                  onSwap={function(e) { 
                    e.stopPropagation();
                    swapMeal(selectedDate, meal.meal_type, meal.plan_id, e, meal.leftover_group_id, meal.leftover_role);
                  }}
                  onRemove={function(e) { 
                    e.stopPropagation();
                    removeMeal(meal.plan_id, meal.meal_name, e, meal.leftover_role);
                  }}
                  onFavorite={function(e) { toggleFavorite(meal.meal_id, meal.meal_name, e); }}
                  isLoading={{
                    swap: isSwapping,
                    remove: isRemoving,
                    favorite: false
                  }}
                />
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 bg-card border border-border/40 rounded-xl">
            <p className="text-sm text-muted-foreground mb-3">No meals planned yet</p>
            {/* Opens the planner (same as the "Plan Day" action), so it carries
                the same label — "Add Meal" is reserved for the /log-food path. */}
            <Button
              onClick={function() { setShowPlanLauncher(true); }}
              size="sm"
              variant="outline"
              className="rounded-full"
              data-testid="daily-empty-plan-day-btn"
            >
              <Plus size={14} className="mr-1" />
              Plan Day
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Render macro summary for a specific date (reusable for weekly expanded days)
  function renderDateMacroSummary(dateStr) {
    if (!summary) return null;
    // Nutrition setup incomplete: same setup CTA as the Daily view — never a
    // fabricated goal or "/2000"-style fake target.
    if (summary.targets_ready !== true) {
      return renderTargetsPrompt();
    }

    var goal = summary.daily_goal;
    var dayMacros = getDateMacros(dateStr);
    var dateGoal = summary.goal;

    var macroItems = [
      { label: 'Cal', planned: Math.round(dayMacros.calories), goal: goal.calories, macro: 'calories' },
      { label: 'P', planned: Math.round(dayMacros.protein), goal: goal.protein, macro: 'protein' },
      { label: 'C', planned: Math.round(dayMacros.carbs), goal: goal.carbs, macro: 'carbs' },
      { label: 'F', planned: Math.round(dayMacros.fat), goal: goal.fat, macro: 'fat' }
    ];
    
    return (
      <div className="bg-secondary/30 rounded-lg p-2 mb-3">
        <div className="grid grid-cols-4 gap-2">
          {macroItems.map(function(item) {
            var pct = item.goal > 0 ? Math.round((item.planned / item.goal) * 100) : 0;
            var barWidth = Math.min(pct, 100);
            
            return (
              <div key={item.label} className="text-center">
                <div className="h-1 bg-secondary rounded-full overflow-hidden mb-0.5">
                  <div 
                    className={'h-full rounded-full transition-all ' + getMacroBarColor(pct, item.macro, dateGoal)}
                    style={{ width: barWidth + '%' }}
                  />
                </div>
                <p className="text-xs font-mono">
                  <span>{item.planned}</span>
                  <span className="text-muted-foreground">/{item.goal}</span>
                </p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Render day detail section - flat meal list (like Home screen)
  function renderDayDetail(dateStr) {
    var meals = getMealsForDate(dateStr);
    
    // Sort meals by meal type order: Breakfast → Lunch → Dinner → Snack
    var sortedMeals = [];
    for (var i = 0; i < mealTypeOrder.length; i++) {
      var mealType = mealTypeOrder[i];
      var typeMeals = meals.filter(function(m) { return m.meal_type === mealType; });
      sortedMeals = sortedMeals.concat(typeMeals);
    }
    
    if (sortedMeals.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground mb-3">No meals planned for this day</p>
          <Button
            onClick={function() {
              exitFixModeIfLeavingTarget(dateStr);
              setPlanLocation({ view: 'daily', date: dateStr });
              setTimeout(function() { setShowPlanLauncher(true); }, 100);
            }}
            size="sm"
            variant="outline"
            className="rounded-full"
            data-testid={'day-empty-plan-day-btn-' + dateStr}
          >
            <Plus size={14} className="mr-1" />
            Plan Day
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {renderDateMacroSummary(dateStr)}
        
        {/* Flat meal list - no dropdowns */}
        {sortedMeals.map(function(meal) {
          var isFavorite = favHas(meal.meal_id);
          var isRemoving = actionLoading === meal.plan_id + '-remove';
          var isSwapping = actionLoading === meal.plan_id + '-swap';
          
          return (
            <MealCard
              key={meal.plan_id}
              meal={{
                meal_id: meal.meal_id,
                name: meal.meal_name,
                calories: (meal.macros && meal.macros.calories) || 0,
                protein: (meal.macros && meal.macros.protein) || 0,
                carbs: (meal.macros && meal.macros.carbs) || 0,
                fat: (meal.macros && meal.macros.fat) || 0,
                image_url: meal.image_url
              }}
              mealType={meal.meal_type}
              planId={meal.plan_id}
              status={meal.status}
              isFavorite={isFavorite}
              variant="plan"
              showLoggedBadge={meal.status === 'completed'}
              leftoverRole={meal.leftover_role}
              onCardClick={function() { goToMealView(meal.meal_id, meal.meal_type, meal.plan_id, meal); }}
              onSwap={function(e) { 
                e.stopPropagation();
                swapMeal(dateStr, meal.meal_type, meal.plan_id, e, meal.leftover_group_id, meal.leftover_role);
              }}
              onRemove={function(e) { 
                e.stopPropagation();
                removeMeal(meal.plan_id, meal.meal_name, e, meal.leftover_role);
              }}
              onFavorite={function(e) { toggleFavorite(meal.meal_id, meal.meal_name, e); }}
              isLoading={{
                swap: isSwapping,
                remove: isRemoving,
                favorite: false
              }}
            />
          );
        })}
        
        {/* Action buttons - Add more meals & Regenerate */}
        <div className="flex gap-2 mt-3">
          <Button
            onClick={function() {
              navigate(buildUrl('/log-food', { date: dateStr, return: planReturn }));
            }}
            size="sm"
            variant="outline"
            className="flex-1 rounded-lg text-xs border-dashed"
          >
            <Plus size={14} className="mr-1" />
            Add Meal
          </Button>
          <Button
            onClick={function() { handleRegenerateDayClick(dateStr); }}
            size="sm"
            variant="outline"
            className="flex-1 rounded-lg text-xs"
            data-testid={'regenerate-day-' + dateStr}
          >
            <RotateCcw size={14} className="mr-1" />
            Regenerate
          </Button>
        </div>
      </div>
    );
  }

  // Render Weekly Overview section — planning status + action card
  function renderWeeklyOverview(weekData, dailyGoal) {
    // Determine "remaining days in the week including today" and their statuses.
    // Reuses existing per-day fields: `status` ('complete' | 'needs-adjustment' | 'partial' | 'none'),
    // `macroStatus`, `totalMeals`, `isToday`, `date`.
    var remainingDays = [];
    var seenToday = false;
    for (var i = 0; i < weekData.length; i++) {
      if (weekData[i].isToday) seenToday = true;
      if (seenToday) remainingDays.push(weekData[i]);
    }
    // Fallback: if the week is in the past/future and no isToday match, treat all 7 as remaining
    if (remainingDays.length === 0) remainingDays = weekData.slice();

    var targetsReady = !!(summary && summary.targets_ready);
    var plannedRemaining = remainingDays.filter(function(d) { return d.totalMeals > 0; });
    var unplannedRemaining = remainingDays.filter(function(d) { return d.totalMeals === 0; });
    // Without real, complete targets a planned day can never be classified
    // "off-target" — every day's macroStatus is already 'none' in that case
    // (see renderWeeklyView), but this guard keeps the fact explicit here too.
    var needsAdjustmentDays = targetsReady
      ? remainingDays.filter(function(d) { return d.totalMeals > 0 && d.macroStatus !== 'on-track'; })
      : [];

    var allPlanned = unplannedRemaining.length === 0;
    var allAligned = targetsReady && needsAdjustmentDays.length === 0 && allPlanned;

    // Top-level status text
    var headlineText;
    var headlineClass;
    var headlineIcon;
    var reasonText = null;
    if (remainingDays.length === 0) {
      headlineText = 'Week complete';
      headlineClass = 'text-accent';
      headlineIcon = <Check size={16} className="text-accent" />;
    } else if (allAligned) {
      headlineText = "You're on track this week";
      headlineClass = 'text-accent';
      headlineIcon = <Check size={16} className="text-accent" />;
    } else {
      headlineText = 'Plan needs adjustment';
      headlineClass = 'text-amber-600';
      headlineIcon = <AlertCircle size={16} className="text-amber-500" />;
      // Short reason — prioritize unplanned over off-target
      if (needsAdjustmentDays.length > 0 && unplannedRemaining.length > 0) {
        reasonText = unplannedRemaining.length + ' unplanned · ' + needsAdjustmentDays.length + ' off-target';
      } else if (unplannedRemaining.length > 0) {
        reasonText = unplannedRemaining.length + ' unplanned day' + (unplannedRemaining.length !== 1 ? 's' : '') + ' remaining';
      } else if (needsAdjustmentDays.length === 1) {
        reasonText = '1 day needs adjustment';
      } else if (needsAdjustmentDays.length > 1) {
        reasonText = needsAdjustmentDays.length + ' days need adjustment';
      }
    }

    // Fix Plan handler — builds an ordered queue of the problem days and enters fix mode.
    // Priority ordering: unplanned first, then planned days outside the accepted macro range
    // (needsAdjustmentDays). Slot coverage alone ('partial') never queues an on-track day.
    // All are remaining days.
    function handleFixPlan() {
      var queue = []
        .concat(unplannedRemaining)
        .concat(needsAdjustmentDays)
        .map(function(d) { return d.date; });
      // De-duplicate while preserving order
      var seen = {};
      var orderedDates = [];
      for (var q = 0; q < queue.length; q++) {
        if (!seen[queue[q]]) { seen[queue[q]] = true; orderedDates.push(queue[q]); }
      }
      if (orderedDates.length === 0) return;

      setFixModeQueue(orderedDates);
      setFixModeIndex(0);
      setFixModeHandled(false);
      setFixModeActive(true);

      // Empty day -> Plan Food launcher; planned day -> Regenerate Day
      // (Keep any meals? -> Review -> Save), decided from that day's own data.
      openFixDay(orderedDates[0]);
    }

    return (
      <div className="bg-card border border-border/60 rounded-2xl p-5 mb-4 shadow-sm" data-testid="weekly-overview">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-base">Weekly Overview</h3>
            <p className="text-xs text-muted-foreground">{getWeekRangeTextLocal()}</p>
          </div>
        </div>

        {/* Planning progress */}
        <div className="flex items-baseline justify-between mb-3" data-testid="weekly-planning-progress">
          <span className="text-[13px] font-medium text-muted-foreground">Planning progress</span>
          <span className="text-[15px] font-semibold tabular-nums">
            {plannedRemaining.length}
            <span className="text-muted-foreground font-medium"> / {remainingDays.length} days planned</span>
          </span>
        </div>

        {/* Goal support status — requires real, complete targets to compute at
            all (On Track / Needs Adjustment / Fix Days are all goal-aware
            classifications). Without them, show the setup CTA instead of a
            fabricated or "Maintain"-assumed status (see AGENTS.md pre-launch
            audit nutrition-readiness follow-up). */}
        {!targetsReady ? (
          renderTargetsPrompt()
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1" data-testid="weekly-status-headline">
              {headlineIcon}
              <span className={'text-sm font-medium ' + headlineClass}>{headlineText}</span>
            </div>

            {/* Reason (only when adjustment needed) */}
            {reasonText && (
              <p className="text-xs text-muted-foreground mb-4" data-testid="weekly-status-reason">{reasonText}</p>
            )}
            {!reasonText && <div className="mb-4" />}

            {/* Fix Days action — shown only when adjustment is needed and there's a target to fix */}
            {!allAligned && (unplannedRemaining.length > 0 || needsAdjustmentDays.length > 0) && (
              <Button
                onClick={handleFixPlan}
                className="w-full h-10 rounded-full bg-accent text-white hover:bg-accent/90"
                data-testid="fix-plan-btn"
              >
                Fix Days
              </Button>
            )}
          </>
        )}
      </div>
    );
  }

  // Render Weekly View - Status overview calendar + expandable day details
  function renderWeeklyView() {
    var weekDates = getWeekDates(weekOffset);
    // Without real, complete targets there is nothing to classify against —
    // dailyGoal falls back to a zero object purely so the percentage/deficit
    // math below stays crash-safe; it is never treated as a real target or
    // shown to the user (see AGENTS.md pre-launch audit nutrition-readiness
    // follow-up).
    var targetsReady = !!(summary && summary.targets_ready);
    var dailyGoal = targetsReady ? summary.daily_goal : { calories: 0, protein: 0, carbs: 0, fat: 0 };

    // Build week data for calendar
    var weekData = weekDates.map(function(dateStr) {
      var dayMeals = getMealsForDate(dateStr);
      var dayMacros = getDateMacros(dateStr);
      var today = isToday(dateStr);

      // Determine planning status (considering skipped slots)
      var mealTypesPlanned = 0;
      var mealTypesSkipped = 0;
      for (var i = 0; i < mealTypeOrder.length; i++) {
        var type = mealTypeOrder[i];
        var hasMeals = dayMeals.some(function(m) { return m.meal_type === type; });
        var isSkipped = isSlotSkipped(dateStr, type);
        if (hasMeals) mealTypesPlanned++;
        if (isSkipped) mealTypesSkipped++;
      }

      // Determine macro status using shared goal-aware logic. Never
      // classify (nor assume "Maintain") without real, complete targets.
      var macroStatus = (targetsReady && dayMeals.length > 0)
        ? getMacroStatus(dayMacros, dailyGoal, summary.goal)
        : 'none';
      var macrosOnTrack = macroStatus === 'on-track';
      
      // A day is truly "complete" only if:
      // 1. All meal slots are filled or skipped AND
      // 2. Macros are within On Track range
      var allSlotsFilled = mealTypesPlanned + mealTypesSkipped === 4;
      
      var status = 'none';
      if (allSlotsFilled && macrosOnTrack) {
        status = 'complete';
      } else if (allSlotsFilled && !macrosOnTrack) {
        status = 'needs-adjustment'; // Slots filled but macros off
      } else if (mealTypesPlanned > 0 || mealTypesSkipped > 0) {
        status = 'partial';
      }
      
      // Calculate macro percentages for display
      var calPct = dailyGoal.calories > 0 ? Math.round((dayMacros.calories / dailyGoal.calories) * 100) : 0;
      var proteinPct = dailyGoal.protein > 0 ? Math.round((dayMacros.protein / dailyGoal.protein) * 100) : 0;
      var calorieDeficit = Math.max(0, dailyGoal.calories - dayMacros.calories);
      var proteinDeficit = Math.max(0, dailyGoal.protein - dayMacros.protein);
      var calorieExcess = Math.max(0, dayMacros.calories - dailyGoal.calories);
      
      return {
        date: dateStr,
        dayName: new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: new Date(dateStr + 'T00:00:00').getDate(),
        isToday: today,
        totalMeals: dayMeals.length,
        macros: dayMacros,
        status: status,
        macroStatus: macroStatus,
        calPct: calPct,
        proteinPct: proteinPct,
        mealTypesPlanned: mealTypesPlanned,
        mealTypesSkipped: mealTypesSkipped,
        allSlotsFilled: allSlotsFilled,
        macrosOnTrack: macrosOnTrack,
        calorieDeficit: calorieDeficit,
        proteinDeficit: proteinDeficit,
        calorieExcess: calorieExcess
      };
    });

    // Render calendar day cells (status overview only)
    var calendarCells = weekData.map(function(day) {
      var isExpanded = expandedDays[day.date];
      var hasNoMeals = day.totalMeals === 0;
      var statusDisplay = hasNoMeals ? null : getStatusDisplay(day.macroStatus);
      var borderColor = isExpanded ? 'border-accent' : day.isToday ? 'border-accent/50' : 'border-border/40';
      
      // Handler for empty day click - navigate to Daily and open planner
      function handleEmptyDayClick() {
        exitFixModeIfLeavingTarget(day.date);
        setPlanLocation({ view: 'daily', date: day.date });
        // Small delay to allow mode switch, then open planner
        setTimeout(function() { setShowPlanLauncher(true); }, 100);
      }

      // Handler for filled day click - navigate to Daily view for that day
      function handleFilledDayClick() {
        exitFixModeIfLeavingTarget(day.date);
        setPlanLocation({ view: 'daily', date: day.date });
      }
      
      return (
        <button
          key={'cal-' + day.date}
          onClick={hasNoMeals ? handleEmptyDayClick : handleFilledDayClick}
          className={'flex-1 p-2 rounded-lg border-2 transition-all hover:border-accent/50 ' + borderColor + (isExpanded ? ' bg-accent/5' : '')}
          data-testid={'week-day-' + day.date}
        >
          {/* Day header */}
          <div className={'text-center mb-1 ' + (day.isToday ? 'text-accent font-semibold' : 'text-muted-foreground')}>
            <p className="text-xs">{day.dayName}</p>
            <p className="text-lg font-bold">{day.dayNum}</p>
          </div>
          
          {/* Status icon */}
          <div className="flex items-center justify-center py-2">
            {hasNoMeals ? (
              <div className="w-8 h-8 rounded-full bg-accent/15 flex items-center justify-center">
                <Plus size={16} className="text-accent" />
              </div>
            ) : day.macroStatus === 'on-track' ? (
              <div className="w-8 h-8 rounded-full bg-accent/15 flex items-center justify-center">
                <Check size={16} className="text-accent" />
              </div>
            ) : day.macroStatus === 'slightly-off' ? (
              <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center">
                <AlertCircle size={16} className="text-amber-500" />
              </div>
            ) : day.macroStatus === 'needs-adjustment' ? (
              <div className="w-8 h-8 rounded-full bg-red-500/15 flex items-center justify-center">
                <AlertCircle size={16} className="text-red-500" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center">
                <AlertCircle size={16} className="text-muted-foreground" />
              </div>
            )}
          </div>
        </button>
      );
    });

    // Pre-render day breakdown rows (collapsed by default)
    var dayBreakdownRows = weekData.map(function(day) {
      var isExpanded = expandedDays[day.date];
      var statusText = 'Empty';
      // Use shared 3-tier status display
      var dayStatusDisplay = day.totalMeals > 0 ? getStatusDisplay(day.macroStatus) : null;
      var macroStatusText = dayStatusDisplay ? dayStatusDisplay.label : '';
      var macroStatusColor = dayStatusDisplay ? dayStatusDisplay.textColor : 'text-muted-foreground';
      
      var dayColor = 'text-muted-foreground';
      if (day.status === 'complete' || day.status === 'needs-adjustment' || day.status === 'partial') {
        statusText = 'Plan Made';
        dayColor = macroStatusColor;
      }
      
      return (
        <div key={'breakdown-' + day.date} className="border border-border/30 rounded-lg overflow-hidden">
          {/* Collapsed row */}
          <div
            className={'w-full flex items-center justify-between p-3 transition-colors cursor-pointer ' + (isExpanded ? 'bg-accent/5' : 'bg-card hover:bg-secondary/20')}
            data-testid={'day-row-' + day.date}
          >
            <div 
              className="flex items-center gap-3 flex-1"
              onClick={function() { toggleDay(day.date); }}
            >
              <div className={'w-10 text-center ' + (day.isToday ? 'text-accent' : '')}>
                <p className="text-xs text-muted-foreground">{day.dayName}</p>
                <p className="font-bold">{day.dayNum}</p>
              </div>
              <div className="text-left flex-1">
                <p className={'text-xs font-medium ' + dayColor}>{statusText}</p>
                {day.totalMeals > 0 && (
                  <p className={'text-xs ' + dayColor}>{macroStatusText}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="text-right cursor-pointer"
                onClick={function() { toggleDay(day.date); }}
              >
                <p className="text-sm font-mono text-foreground">{Math.round(day.macros.calories)} cal</p>
                <p className="text-xs text-muted-foreground">{Math.round(day.macros.protein)}g P</p>
              </div>
              <div onClick={function() { toggleDay(day.date); }}>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>
          </div>
          
          {/* Expanded content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-3 border-t border-border/20 bg-background">
                  {renderDayDetail(day.date)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    });

    return (
      <div className="space-y-4">
        {/* Weekly Overview */}
        {renderWeeklyOverview(weekData, dailyGoal)}
        
        {/* Status Overview Calendar */}
        <div className="bg-card border border-border/40 rounded-xl p-3" data-testid="weekly-calendar">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-muted-foreground">Weekly Status</span>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Check size={12} className="text-accent" />
                Planned
              </span>
              <span className="flex items-center gap-1">
                <Plus size={12} className="text-accent" />
                Plan
              </span>
            </div>
          </div>
          
          {/* Calendar grid: fixed 7 equal columns so day cells cannot overflow
              the card width on narrow iPhones (the previous flex+flex-1 layout
              let wide cells push the strip off the right edge of the viewport). */}
          <div className="grid grid-cols-7 gap-1">
            {calendarCells}
          </div>
        </div>
        
        {/* Day Breakdown List */}
        <div className="space-y-2" data-testid="day-breakdown">
          <h3 className="text-xs font-medium text-muted-foreground px-1">Daily Breakdown</h3>
          {dayBreakdownRows}
        </div>
      </div>
    );
  }

  // Pre-render grocery items
  var groceryItems = [];
  for (var k = 0; k < groceryList.length; k++) {
    var item = groceryList[k];
    var isChecked = checkedItems[item.display];
    
    groceryItems.push(
      <div
        key={k}
        onClick={toggleGroceryItem.bind(null, item.display)}
        className={'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ' + (isChecked ? 'bg-secondary/30 border-border/20 opacity-60' : 'bg-card border-border/40 hover:border-accent/30')}
      >
        <div className={'w-5 h-5 rounded border-2 flex items-center justify-center transition-all ' + (isChecked ? 'bg-accent border-accent' : 'border-border/60')}>
          {isChecked && <Check size={12} className="text-white" />}
        </div>
        <span className={'flex-1 text-sm ' + (isChecked ? 'line-through text-muted-foreground' : '')}>{item.display}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* Header with Mode Toggle */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        {/* NOTE: px-4 pb-4 (no pt) — `header-safe-top` on the parent already
            provides the single safe-area-inset-top + 8px top offset. */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-heading font-bold text-xl">Plan</h1>
            
            <div className="flex items-center gap-2">
              {/* Daily / Weekly Toggle */}
              <div className="flex bg-secondary rounded-full p-1">
                <button
                  onClick={function() { setMode('daily'); }}
                  className={'px-4 py-1.5 rounded-full text-sm font-medium transition-all ' + (mode === 'daily' ? 'bg-accent text-white' : 'text-muted-foreground')}
                  data-testid="mode-daily"
                >
                  Daily
                </button>
                <button
                  onClick={function() { if (fixModeActive) exitFixMode(); setMode('weekly'); }}
                  className={'px-4 py-1.5 rounded-full text-sm font-medium transition-all ' + (mode === 'weekly' ? 'bg-accent text-white' : 'text-muted-foreground')}
                  data-testid="mode-weekly"
                >
                  Weekly
                </button>
              </div>
              
              {/* More menu with Clear actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-full h-9 w-9 p-0" data-testid="plan-more-menu">
                    <MoreHorizontal size={18} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {mode === 'daily' ? (
                    <DropdownMenuItem 
                      onClick={function() { setClearConfirm('day'); }}
                      className="text-red-500 focus:text-red-500"
                      data-testid="clear-day-menu"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Clear Day
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem 
                        onClick={function() { setClearConfirm('day'); }}
                        className="text-red-500 focus:text-red-500"
                        data-testid="clear-today-menu"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Clear Today
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={function() { setClearConfirm('week'); }}
                        className="text-red-500 focus:text-red-500"
                        data-testid="clear-week-menu"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Clear Entire Week
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Date Navigation */}
          {mode === 'daily' ? (
            <div className="flex items-center justify-between">
              <Button onClick={prevDay} variant="ghost" size="sm" className="rounded-full" data-testid="prev-day">
                <ChevronLeft size={18} />
              </Button>
              <div className="flex items-center gap-2">
                <div className="text-center">
                  <p className="font-heading font-semibold">{formatDate(selectedDate)}</p>
                  {isToday(selectedDate) ? (
                    <p className="text-xs text-accent">Today</p>
                  ) : (
                    <button 
                      onClick={function() { exitFixModeIfLeavingTarget(getTodayString()); setPlanLocation({ view: 'daily', date: getTodayString() }); }}
                      className="text-xs text-accent hover:underline"
                      data-testid="go-today-btn"
                    >
                      Return to Today
                    </button>
                  )}
                </div>
                {/* Calendar Icon - Jump to date */}
                <Button
                  onClick={function() { setShowJumpPicker(true); }}
                  variant="ghost"
                  size="icon"
                  className="rounded-full h-8 w-8"
                  data-testid="plan-calendar-btn"
                  aria-label="Jump to date"
                >
                  <Calendar size={16} />
                </Button>
              </div>
              <Button onClick={nextDay} variant="ghost" size="sm" className="rounded-full" data-testid="next-day">
                <ChevronRight size={18} />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Button onClick={prevWeek} variant="ghost" size="sm" className="rounded-full" data-testid="prev-week">
                <ChevronLeft size={18} />
              </Button>
              <div className="flex items-center gap-2">
                <div className="text-center">
                  <p className="font-heading font-semibold text-sm">{getWeekRangeTextLocal()}</p>
                  {weekOffset === 0 ? (
                    <p className="text-xs text-accent">This Week</p>
                  ) : (
                    <button 
                      onClick={function() { setPlanLocation({ view: 'weekly', date: getTodayString() }); }}
                      className="text-xs text-accent hover:underline"
                      data-testid="go-this-week-btn"
                    >
                      Return to This Week
                    </button>
                  )}
                </div>
                {/* Calendar Icon - Jump to week */}
                <Button
                  onClick={function() { setShowJumpPicker(true); }}
                  variant="ghost"
                  size="icon"
                  className="rounded-full h-8 w-8"
                  data-testid="plan-calendar-btn"
                  aria-label="Jump to week"
                >
                  <Calendar size={16} />
                </Button>
              </div>
              <Button onClick={nextWeek} variant="ghost" size="sm" className="rounded-full" data-testid="next-week">
                <ChevronRight size={18} />
              </Button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="p-4 space-y-6">
          {/* New THREE BUTTON LAYOUT - Equal width, outline style */}
          <div className="grid grid-cols-3 gap-2">
            {/* Button 1: Plan Week / Plan Day */}
            <Button
              onClick={handlePlanButtonClick}
              disabled={planningInProgress}
              variant="outline"
              className="h-12 rounded-xl border-accent text-accent hover:bg-accent/10 font-medium text-sm"
              data-testid="plan-btn"
            >
              {planningInProgress ? (
                <span className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <>
                  <Calendar size={16} className="mr-1.5" />
                  {mode === 'daily' ? 'Plan Day' : 'Plan Week'}
                </>
              )}
            </Button>
            
            {/* Button 2: Add Meal */}
            <Button
              onClick={handleAddMealClick}
              variant="outline"
              className="h-12 rounded-xl border-accent text-accent hover:bg-accent/10 font-medium text-sm"
              data-testid="add-meal-btn"
            >
              <Plus size={16} className="mr-1.5" />
              Add Meal
            </Button>
            
            {/* Button 3: Regenerate */}
            <Button
              onClick={function() { handleRegenerateClick(); }}
              disabled={planningInProgress}
              variant="outline"
              className="h-12 rounded-xl border-accent text-accent hover:bg-accent/10 font-medium text-sm"
              data-testid="regenerate-btn"
            >
              <RefreshCw size={16} className="mr-1.5" />
              Regenerate
            </Button>
          </div>

          {/* Meals Section - Expandable */}
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
            data-testid="meals-section"
          >
            <h2 className="font-heading font-semibold">
              {mode === 'daily' ? 'Meals' : 'This Week'}
            </h2>
            
            {summaryError ? (
              <div
                className="bg-card border border-destructive/40 rounded-xl p-4 space-y-3"
                data-testid="plan-summary-error-card"
                role="alert"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-destructive shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">Couldn't load your plan</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Your meals are saved — they just couldn't load right now. Check your
                      connection and try again.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={function() { fetchData(); }}
                    size="sm"
                    variant="outline"
                    className="rounded-lg text-xs"
                    data-testid="plan-summary-error-retry"
                  >
                    <RefreshCw size={14} className="mr-1" />
                    Retry
                  </Button>
                  <Button
                    onClick={function() { setShowSummaryErrorDetails(!showSummaryErrorDetails); }}
                    size="sm"
                    variant="ghost"
                    className="rounded-lg text-xs text-muted-foreground"
                    data-testid="plan-summary-error-details-toggle"
                  >
                    {showSummaryErrorDetails ? 'Hide details' : 'Show details'}
                  </Button>
                </div>
                {showSummaryErrorDetails && (
                  <div
                    className="bg-secondary/30 rounded-lg p-3 font-mono text-xs text-muted-foreground break-words"
                    data-testid="plan-summary-error-details"
                  >
                    <div>status: {summaryError.status === null ? 'network' : summaryError.status}</div>
                    {summaryError.code && <div>code: {summaryError.code}</div>}
                    <div>message: {summaryError.message}</div>
                  </div>
                )}
              </div>
            ) : (
              mode === 'daily' ? renderDailyView() : renderWeeklyView()
            )}
          </motion.div>

          {/* My Pantry Card */}
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm"
          >
            <button
              onClick={function() { if (fixModeActive) exitFixMode(); navigate('/pantry'); }}
              className="w-full p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors"
              data-testid="pantry-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Package size={20} className="text-amber-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-heading font-semibold">My Pantry</h3>
                  <p className="text-xs text-muted-foreground">
                    {pantryItems.length > 0 
                      ? pantryItems.length + ' items saved' 
                      : 'Track ingredients you have'}
                  </p>
                </div>
              </div>
              <ChevronRight size={20} className="text-muted-foreground" />
            </button>
          </motion.div>

          {/* Grocery Section - At Bottom */}
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border/40 rounded-xl p-4 shadow-sm"
            data-testid="grocery-section"
          >
            <button
              type="button"
              onClick={function() {
                if (isFree) { goToGrocery(); return; }
                setGroceryOpen(function(open) { return !open; });
              }}
              aria-expanded={isFree ? undefined : groceryOpen}
              aria-controls={isFree ? undefined : 'plan-grocery-preview'}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <ShoppingBag size={20} className="text-accent" />
                <h2 className="font-heading font-semibold">Grocery List</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {isFree || (groceryOpen && groceryCurrent && !groceryLoading && !groceryError)
                    ? Object.keys(checkedItems).length + ' / ' + groceryList.length + ' items'
                    : groceryOpen ? '' : 'Show list'}
                </span>
                {isFree ? <ChevronRight size={16} className="text-muted-foreground" />
                  : groceryOpen ? <ChevronUp size={16} className="text-muted-foreground" />
                  : <ChevronDown size={16} className="text-muted-foreground" />}
              </div>
            </button>
            
            {groceryOpen && !isFree && (
              <div id="plan-grocery-preview" className="mt-4">
                {groceryLoading ? (
                  <p role="status" className="text-center text-muted-foreground py-4">Loading groceries...</p>
                ) : groceryError ? (
                  <div role="alert" className="text-center py-4">
                    <p className="text-sm text-muted-foreground">{groceryError.message}</p>
                    {groceryError.retryable && (
                      <Button variant="ghost" size="sm" className="mt-2 text-accent"
                        onClick={function() { setGroceryRefresh(function(n) { return n + 1; }); }}>
                        Retry
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
            {groceryList.length > 0 ? (
              <>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {groceryItems.slice(0, 5)}
                </div>
                {groceryList.length > 5 && (
                  <Button
                    onClick={goToGrocery}
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2 text-accent"
                  >
                    View all {groceryList.length} items
                  </Button>
                )}
              </>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                No groceries needed for selected {mode === 'daily' ? 'day' : 'week'}
              </p>
            )}
            
            {groceryList.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border/20">
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-300"
                    style={{ width: (Object.keys(checkedItems).length / groceryList.length * 100) + '%' }}
                  />
                </div>
              </div>
            )}
                  </>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Plan Food Launcher Modal - kept for backward compatibility */}
      <PlanFoodLauncher
        isOpen={showPlanLauncher}
        onClose={function() {
          setShowPlanLauncher(false);
          // Always clear the preset date on close so it can never leak into
          // the next, unrelated (non-Fix-Days) launcher open.
          setPlanLauncherPresetDate(null);
          setPlanLauncherPresetSummary(null);
          // Immediately refresh the plan + grocery list so newly added meals
          // show up in the Daily view without the user having to leave and
          // come back. The launcher can mutate the plan from inside the modal
          // (direct add / logged) without unmounting PlanningHub, so its own
          // useEffect would never re-fire.
          fetchData();
        }}
        onPlanGenerated={function(handledDates) {
          // Guided Fix Days: a successful generation from this launcher (the
          // empty-day path) never round-trips through PlanReview.handleSave,
          // so it must set the same one-shot continuation signal directly —
          // but only for the date(s) this launcher actually generated. The
          // consumer verifies the current queue target is among them before
          // advancing (see AGENTS.md pre-launch audit, finding FIXDAYS-1) —
          // an unrelated date generated while Fix Mode is merely active must
          // never advance the queue.
          if (fixModeActive) {
            setFixModeAdvancePending(handledDates || []);
          }
        }}
        context={mode === 'daily' ? 'daily' : 'weekly'}
        weekDates={getWeekDates(weekOffset)}
        specificDate={selectedDate}
        presetDate={planLauncherPresetDate}
        // Fix Days on a day with saved meals: Smart Fill opens Regenerate Day
        // (the same path a planned queued day always used) once the page
        // summary holds that day's rows — see the pendingFixRegenDate effect.
        onPresetSmartFill={planLauncherPresetSummary ? function(dateStr) {
          setSummary(planLauncherPresetSummary);
          setPendingFixRegenDate(dateStr);
        } : undefined}
        returnTo={planReturn}
      />

      {/* Jump-to picker: date for daily mode, week list for weekly mode */}
      <JumpToPicker
        isOpen={showJumpPicker}
        mode={mode === 'daily' ? 'date' : 'week'}
        selectedDate={selectedDate}
        weekOffset={weekOffset}
        onSelectDate={function(dateStr) {
          exitFixModeIfLeavingTarget(dateStr);
          setPlanLocation({ view: 'daily', date: dateStr });
        }}
        onSelectWeek={function(off) {
          setPlanLocation({ view: 'weekly', date: addDays(getTodayString(), 7 * off) });
        }}
        onClose={function() { setShowJumpPicker(false); }}
      />

      {/* Clear Confirmation Dialog */}
      <AlertDialog open={clearConfirm !== null} onOpenChange={function(open) { if (!open) setClearConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {clearConfirm === 'week' ? 'Clear Entire Week?' : 'Clear Day?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {clearConfirm === 'week' 
                ? 'This will remove all meals from your entire week plan. This action cannot be undone.'
                : 'This will remove all meals from this day\'s plan. This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={function() { clearConfirm === 'week' ? handleClearWeek() : handleClearDay(); }}
              disabled={clearing}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-clear-btn"
            >
              {clearing ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Clearing...
                </span>
              ) : (
                clearConfirm === 'week' ? 'Clear Week' : 'Clear Day'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* LEFTOVERS: Pair-safe replacement — shared component used by Home too.
          Candidate loading + Cancel are read-only; selecting a recipe atomically
          swaps BOTH rows via the pair-safe swapPair route. */}
      <PairSwapDialog
        context={pairSwapContext}
        onClose={function() { setPairSwapContext(null); }}
        onSwapped={fetchData}
      />

      {/* LEFTOVERS: linked dinner<->lunch pair removal confirmation */}
      <AlertDialog open={leftoverRemoveConfirm !== null} onOpenChange={function(open) { if (!open) setLeftoverRemoveConfirm(null); }}>
        <AlertDialogContent data-testid="leftover-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove linked meals?</AlertDialogTitle>
            <AlertDialogDescription>
              This dinner and its next-day leftover lunch are cooked together as one batch.
              Removing {leftoverRemoveConfirm ? leftoverRemoveConfirm.mealName : 'this meal'} will
              remove both the dinner and the linked leftover lunch. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={function() {
                if (leftoverRemoveConfirm) {
                  doRemoveMeal(leftoverRemoveConfirm.planId, leftoverRemoveConfirm.mealName);
                }
                setLeftoverRemoveConfirm(null);
              }}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-leftover-remove-btn"
            >
              Remove both
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Plan Week Modal (Weekly mode - shows "Plan This Week" or "Custom" options) */}
      <AnimatePresence>
        {showPlanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={function() { setShowPlanModal(false); }}
            data-testid="plan-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl relative"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="plan-modal"
            >
              {/* Close button */}
              <button
                onClick={function() { setShowPlanModal(false); }}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                data-testid="plan-modal-close"
              >
                <X size={20} />
              </button>
              
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Calendar size={24} className="text-accent" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="font-heading font-bold text-lg text-center mb-2">
                Plan Week
              </h3>
              
              {/* Content based on selection */}
              {planModalOption === null ? (
                <>
                  {/* Description */}
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Choose a date range to auto-fill with meals
                  </p>
                  
                  {/* Options */}
                  <div className="space-y-3">
                    <Button
                      onClick={handlePlanThisWeek}
                      className="w-full h-12 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90"
                      data-testid="plan-this-week-btn"
                    >
                      <Calendar size={18} className="mr-2" />
                      Plan This Week
                    </Button>
                    <Button
                      onClick={function() { setPlanModalOption('custom'); }}
                      variant="outline"
                      className="w-full h-12 rounded-xl font-semibold border-accent text-accent"
                      data-testid="plan-custom-btn"
                    >
                      <Sparkles size={18} className="mr-2" />
                      Custom
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Custom date range picker */}
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Select a date range
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={function(e) { setCustomStartDate(e.target.value); }}
                        className="w-full h-11 px-4 rounded-xl border border-border bg-background text-foreground focus:border-accent focus:outline-none"
                        data-testid="custom-start-date"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        min={customStartDate}
                        onChange={function(e) { setCustomEndDate(e.target.value); }}
                        className="w-full h-11 px-4 rounded-xl border border-border bg-background text-foreground focus:border-accent focus:outline-none"
                        data-testid="custom-end-date"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={function() { setPlanModalOption(null); }}
                      variant="outline"
                      className="flex-1 h-11 rounded-xl"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handlePlanCustomRange}
                      disabled={!customStartDate || !customEndDate}
                      className="flex-1 h-11 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90"
                      data-testid="confirm-custom-plan-btn"
                    >
                      Plan Meals
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Meal Modal - Date selection only (weekly mode), then navigate to Log Food */}
      <AnimatePresence>
        {showAddMealModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={function() { setShowAddMealModal(false); }}
            data-testid="add-meal-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl relative"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="add-meal-modal"
            >
              {/* Close button */}
              <button
                onClick={function() { setShowAddMealModal(false); }}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                data-testid="add-meal-modal-close"
              >
                <X size={20} />
              </button>
              
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Plus size={24} className="text-accent" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="font-heading font-bold text-lg text-center mb-2">
                Add Meal
              </h3>
              
              {/* Step 1: Date Selection (quick dates) */}
              {addMealStep === 1 && (
                <>
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Select a date
                  </p>
                  
                  <div className="space-y-3">
                    {getWeekDates(weekOffset).slice(0, 4).map(function(dateStr) {
                      return (
                        <Button
                          key={dateStr}
                          onClick={function() { handleAddMealDateSelect(dateStr); }}
                          variant="outline"
                          className="w-full h-11 rounded-xl font-medium border-border hover:border-accent hover:text-accent"
                          data-testid={'date-option-' + dateStr}
                        >
                          {isToday(dateStr) ? 'Today' : formatShortDate(dateStr)}
                        </Button>
                      );
                    })}
                    <Button
                      onClick={function() { setAddMealStep('datePicker'); }}
                      variant="ghost"
                      className="w-full h-10 text-accent hover:text-accent"
                      data-testid="pick-date-btn"
                    >
                      <Calendar size={16} className="mr-2" />
                      Pick a different date
                    </Button>
                  </div>
                </>
              )}
              
              {/* Step 1b: Date Picker (for custom date) */}
              {addMealStep === 'datePicker' && (
                <>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Select a date
                  </p>
                  
                  <div className="mb-6">
                    <input
                      type="date"
                      value={addMealDate || ''}
                      onChange={function(e) { setAddMealDate(e.target.value); }}
                      className="w-full h-11 px-4 rounded-xl border border-border bg-background text-foreground focus:border-accent focus:outline-none"
                      data-testid="add-meal-date-picker"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={function() { setAddMealStep(1); setAddMealDate(null); }}
                      variant="outline"
                      className="flex-1 h-11 rounded-xl"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={function() { if (addMealDate) handleAddMealDateSelect(addMealDate); }}
                      disabled={!addMealDate}
                      className="flex-1 h-11 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90"
                      data-testid="confirm-date-btn"
                    >
                      Continue
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Regenerate Modal - Multi-step flow with Keep Selections */}
      <AnimatePresence>
        {showRegenerateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseRegenerateModal}
            data-testid="regenerate-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl relative max-h-[85vh] overflow-y-auto"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="regenerate-modal"
            >
              {/* Close button */}
              <button
                onClick={handleCloseRegenerateModal}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground z-10"
              >
                <X size={20} />
              </button>
              
              {/* Regenerate flow now opens directly into AI keep-selection
                  (or 'preferences' if nothing to keep). The legacy
                  Browse-vs-AI 'confirm' step was removed — Regenerate
                  always means AI regeneration. */}
              
              {/* Step 2: Keep Selections - Daily Mode OR Single Day from Weekly */}
              {regenerateStep === 'keep-selection' && (mode === 'daily' || regenerateSingleDay) && (
                <>
                  <h3 className="font-heading font-bold text-lg text-center mb-2">
                    Keep any meals?
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Select meals you want to keep. Unselected meals will be replaced.
                  </p>
                  {(function() {
                    var allPending = getMealsForDate(regenerateSingleDay || selectedDate).filter(function(p) { return p.status === 'pending'; });
                    var linkedMeals = allPending.filter(isLinkedRow);
                    var regularMeals = allPending.filter(function(p) { return !isLinkedRow(p); });
                    var keptRegularCount = regularMeals.filter(function(p) { return mealsToKeep.includes(p.plan_id); }).length;
                    var replaceableCount = regularMeals.length - keptRegularCount;
                    return (
                      <>
                        <div className="space-y-2 max-h-[280px] overflow-y-auto mb-4">
                          {regularMeals.map(function(plan) {
                            var isKept = mealsToKeep.includes(plan.plan_id);
                            var displayName = plan.meal_name || 'Meal';
                            var displayCals = (plan.macros && plan.macros.calories) || 0;
                            var displayProtein = (plan.macros && plan.macros.protein) || 0;
                            return (
                              <button
                                key={plan.plan_id}
                                onClick={function() { toggleMealToKeep(plan.plan_id); }}
                                className={'w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 ' +
                                  (isKept
                                    ? 'border-primary bg-primary/10'
                                    : 'border-transparent bg-secondary/30 hover:bg-secondary/50'
                                  )
                                }
                                data-testid={'keep-meal-' + plan.plan_id}
                              >
                                <div className={'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ' +
                                  (isKept ? 'border-primary bg-primary' : 'border-muted-foreground/30')
                                }>
                                  {isKept && <Check size={14} className="text-white" />}
                                </div>
                                {plan.image_url ? (
                                  <MealImage
                                    src={plan.image_url}
                                    alt={displayName}
                                    className="w-12 h-12 rounded-lg object-cover"
                                    onError={function(e) { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                                    <ChefHat size={20} className="text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 text-left">
                                  <p className="text-xs text-muted-foreground uppercase">{plan.meal_type}</p>
                                  <p className="font-medium text-sm line-clamp-1">{displayName}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {displayCals} cal • {displayProtein}g P
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                          {linkedMeals.map(function(plan) {
                            var displayName = plan.meal_name || 'Meal';
                            var roleLabel = plan.leftover_role === 'cook' ? 'Cook' : 'Leftover';
                            return (
                              <div
                                key={plan.plan_id}
                                className="w-full p-3 rounded-xl border-2 border-primary/20 bg-primary/5 flex items-center gap-3 opacity-90"
                                data-testid={'linked-locked-' + plan.plan_id}
                              >
                                <div className="w-6 h-6 rounded-md border-2 border-primary bg-primary flex items-center justify-center">
                                  <Lock size={12} className="text-white" />
                                </div>
                                {plan.image_url ? (
                                  <MealImage
                                    src={plan.image_url}
                                    alt={displayName}
                                    className="w-12 h-12 rounded-lg object-cover"
                                    onError={function(e) { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                                    <ChefHat size={20} className="text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 text-left">
                                  <div className="flex items-center gap-2">
                                    <p className="text-xs text-muted-foreground uppercase">{plan.meal_type}</p>
                                    <span className={'px-1.5 py-0.5 rounded-full text-[10px] ' + (plan.leftover_role === 'cook' ? 'bg-accent/10 text-accent' : 'bg-secondary text-muted-foreground')}>
                                      {roleLabel}
                                    </span>
                                  </div>
                                  <p className="font-medium text-sm line-clamp-1">{displayName}</p>
                                  <p className="text-xs text-muted-foreground">Kept automatically</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={function(e) {
                                    swapMeal(regenerateSingleDay || selectedDate, plan.meal_type, plan.plan_id, e, plan.leftover_group_id, plan.leftover_role);
                                  }}
                                  className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                                  data-testid={'keep-meal-replace-both-' + plan.plan_id}
                                >
                                  <RefreshCw size={12} /> {plan.leftover_role === 'leftover' ? 'Replace' : 'Replace both'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        {linkedMeals.length > 0 && (
                          <p className="text-xs text-muted-foreground text-center mb-3" data-testid="linked-leftover-note">
                            Linked leftover meals are kept automatically. Tap Replace Both above to change them.
                          </p>
                        )}
                        <div className="text-center text-sm text-muted-foreground mb-4" data-testid="daily-replace-count">
                          {replaceableCount === 0
                            ? '0 meals will be replaced'
                            : replaceableCount === 1
                              ? '1 meal will be replaced'
                              : replaceableCount + ' meals will be replaced'
                          }
                        </div>
                      </>
                    );
                  })()}
                  
                  {/* Never gated on the replaceable count (same as Home): zero
                      replaceable meals still lets the planner ADD meals around
                      logged/kept/linked rows when the day has room; the backend
                      decides, and a retained-only result shows the no-room toast. */}
                  <Button
                    onClick={handleProceedFromKeepSelection}
                    className="w-full h-12 rounded-xl font-semibold"
                    data-testid="proceed-from-keep-btn"
                  >
                    Continue
                  </Button>
                  <Button
                    onClick={handleCloseRegenerateModal}
                    variant="ghost"
                    className="w-full h-10 rounded-xl text-muted-foreground mt-2"
                    data-testid="back-from-keep-btn"
                  >
                    Back
                  </Button>
                </>
              )}
              
              {/* Step 2: Keep Selections - Weekly Mode (Day-by-day walkthrough) */}
              {regenerateStep === 'keep-selection' && mode === 'weekly' && !regenerateSingleDay && (function() {
                var weekDates = getRegenDates();
                var todayStr = getTodayString();
                
                // Filter to only include today and future dates
                var futureDates = weekDates.filter(function(d) { return d >= todayStr; });
                
                // Find current day with meals (only from future dates)
                var currentDateIndex = weeklyWalkthroughDayIndex;
                while (currentDateIndex < futureDates.length) {
                  var checkMeals = getMealsForRegenDate(futureDates[currentDateIndex]).filter(function(p) { return p.status === 'pending'; });
                  if (checkMeals.length > 0) break;
                  currentDateIndex++;
                }

                if (currentDateIndex >= futureDates.length) {
                  // No more days with meals - this shouldn't happen but handle gracefully
                  handleWeeklyWalkthroughSkip();
                  return null;
                }

                var currentDate = futureDates[currentDateIndex];
                var dayMealsAll = getMealsForRegenDate(currentDate).filter(function(p) { return p.status === 'pending'; });
                var dayLinkedMeals = dayMealsAll.filter(isLinkedRow);
                var dayMeals = dayMealsAll.filter(function(p) { return !isLinkedRow(p); });
                var currentDayKept = weeklyMealsToKeepByDate[currentDate] || [];
                var currentDayKeptRegular = dayMeals.filter(function(p) { return currentDayKept.includes(p.plan_id); }).length;

                // Count remaining days with meals (only future dates)
                var remainingDaysWithMeals = 0;
                for (var i = currentDateIndex + 1; i < futureDates.length; i++) {
                  var futMeals = getMealsForRegenDate(futureDates[i]).filter(function(p) { return p.status === 'pending'; });
                  if (futMeals.length > 0) remainingDaysWithMeals++;
                }
                
                return (
                  <>
                    <h3 className="font-heading font-bold text-lg text-center mb-1">
                      {formatDate(currentDate)}
                    </h3>
                    <p className="text-sm text-muted-foreground text-center mb-4">
                      Select meals to keep for this day
                      {remainingDaysWithMeals > 0 && <span className="text-xs"> • {remainingDaysWithMeals} more day{remainingDaysWithMeals > 1 ? 's' : ''} to review</span>}
                    </p>
                    
                    <div className="space-y-2 max-h-[250px] overflow-y-auto mb-4">
                      {dayMeals.map(function(plan) {
                        var isKept = currentDayKept.includes(plan.plan_id);
                        var displayName = plan.meal_name || 'Meal';
                        var displayCals = (plan.macros && plan.macros.calories) || 0;
                        var displayProtein = (plan.macros && plan.macros.protein) || 0;
                        return (
                          <button
                            key={plan.plan_id}
                            onClick={function() { toggleWeeklyMealToKeep(currentDate, plan.plan_id); }}
                            className={'w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 ' + 
                              (isKept 
                                ? 'border-primary bg-primary/10' 
                                : 'border-transparent bg-secondary/30 hover:bg-secondary/50'
                              )
                            }
                            data-testid={'keep-meal-weekly-' + plan.plan_id}
                          >
                            <div className={'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ' +
                              (isKept ? 'border-primary bg-primary' : 'border-muted-foreground/30')
                            }>
                              {isKept && <Check size={14} className="text-white" />}
                            </div>
                            {plan.image_url ? (
                              <MealImage 
                                src={plan.image_url} 
                                alt={displayName}
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                onError={function(e) { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                                <ChefHat size={16} className="text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 text-left">
                              <p className="text-xs text-muted-foreground uppercase">{plan.meal_type}</p>
                              <p className="font-medium text-sm line-clamp-1">{displayName}</p>
                              <p className="text-xs text-muted-foreground">{displayCals} cal</p>
                            </div>
                          </button>
                        );
                      })}
                      {dayLinkedMeals.map(function(plan) {
                        var displayName = plan.meal_name || 'Meal';
                        var roleLabel = plan.leftover_role === 'cook' ? 'Cook' : 'Leftover';
                        return (
                          <div
                            key={plan.plan_id}
                            className="w-full p-3 rounded-xl border-2 border-primary/20 bg-primary/5 flex items-center gap-3 opacity-90"
                            data-testid={'linked-locked-weekly-' + plan.plan_id}
                          >
                            <div className="w-6 h-6 rounded-md border-2 border-primary bg-primary flex items-center justify-center">
                              <Lock size={12} className="text-white" />
                            </div>
                            {plan.image_url ? (
                              <MealImage
                                src={plan.image_url}
                                alt={displayName}
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                onError={function(e) { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                                <ChefHat size={16} className="text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-muted-foreground uppercase">{plan.meal_type}</p>
                                <span className={'px-1.5 py-0.5 rounded-full text-[10px] ' + (plan.leftover_role === 'cook' ? 'bg-accent/10 text-accent' : 'bg-secondary text-muted-foreground')}>
                                  {roleLabel}
                                </span>
                              </div>
                              <p className="font-medium text-sm line-clamp-1">{displayName}</p>
                              <p className="text-xs text-muted-foreground">Kept automatically</p>
                            </div>
                            <button
                              type="button"
                              onClick={function(e) {
                                swapMeal(currentDate, plan.meal_type, plan.plan_id, e, plan.leftover_group_id, plan.leftover_role);
                              }}
                              className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                              data-testid={'keep-meal-replace-both-weekly-' + plan.plan_id}
                            >
                              <RefreshCw size={12} /> {plan.leftover_role === 'leftover' ? 'Replace' : 'Replace both'}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {dayLinkedMeals.length > 0 && (
                      <p className="text-xs text-muted-foreground text-center mb-3" data-testid="linked-leftover-note-weekly">
                        Linked leftover meals are kept automatically. Tap Replace Both above to change them.
                      </p>
                    )}
                    <div className="text-center text-sm text-muted-foreground mb-4" data-testid="weekly-replace-count">
                      {(function() {
                        var wr = dayMeals.length - currentDayKeptRegular;
                        return wr === 0 ? '0 meals will be replaced'
                          : wr === 1 ? '1 meal will be replaced'
                          : wr + ' meals will be replaced';
                      })()}
                    </div>
                    
                    <div className="flex gap-3">
                      {remainingDaysWithMeals > 0 ? (
                        <>
                          <Button
                            onClick={handleWeeklyWalkthroughSkip}
                            variant="outline"
                            className="flex-1 h-11 rounded-xl"
                          >
                            Skip remaining
                          </Button>
                          <Button
                            onClick={handleWeeklyWalkthroughNext}
                            className="flex-1 h-11 rounded-xl font-semibold"
                          >
                            Next day →
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={handleWeeklyWalkthroughNext}
                          className="w-full h-11 rounded-xl font-semibold"
                        >
                          Continue
                        </Button>
                      )}
                    </div>
                    <Button
                      onClick={handleCloseRegenerateModal}
                      variant="ghost"
                      className="w-full h-10 rounded-xl text-muted-foreground mt-2"
                    >
                      Back
                    </Button>
                  </>
                );
              })()}
              
              {/* Step 3: Preferences - Review before generating */}
              {regenerateStep === 'preferences' && (
                <>
                  <h3 className="font-heading font-bold text-lg text-center mb-2">
                    Before we start
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Review your preferences or continue to generate
                  </p>
                  
                  <div className="space-y-3">
                    {/* Cooking Preferences */}
                    <button
                      onClick={handleEditCookingPrefs}
                      className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors"
                      data-testid="edit-cooking-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                          <ChefHat size={20} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium">Cooking Preferences</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedCookware.length > 0 
                              ? selectedCookware.join(', ') + (selectedCookTime ? ' \u2022 ' + selectedCookTime + ' min max' : '')
                              : 'Not set - tap to configure'}
                          </p>
                        </div>
                        <Edit2 size={16} className="text-muted-foreground" />
                      </div>
                    </button>
                    
                    {/* My Pantry - editable */}
                    <button
                      onClick={handleEditPantry}
                      className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors"
                      data-testid="edit-pantry-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                          <Package size={20} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium">My Pantry</p>
                          <p className="text-xs text-muted-foreground">
                            {pantryItems.length > 0 
                              ? pantryItems.length + ' items - auto-prioritized'
                              : 'No items - tap to add'}
                          </p>
                        </div>
                        <Edit2 size={16} className="text-muted-foreground" />
                      </div>
                    </button>
                    
                    {/* Meal Filters */}
                    <div className="border border-border/40 rounded-xl overflow-hidden">
                      <button
                        onClick={function() { setShowFiltersPanel(!showFiltersPanel); }}
                        className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 transition-colors"
                        data-testid="toggle-filters-btn"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                            <Filter size={20} className="text-muted-foreground" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium">Meal Filters</p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedTags.length > 0 || selectedCuisine.length > 0)
                                ? (selectedTags.length > 0 ? selectedTags.length + ' tags' : '') + 
                                  (selectedTags.length > 0 && selectedCuisine.length > 0 ? ' • ' : '') +
                                  (selectedCuisine.length > 0 ? selectedCuisine.length + ' cuisine' : '')
                                : 'Filter by tags, cuisine (optional)'}
                            </p>
                          </div>
                          {showFiltersPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </button>
                      
                      {showFiltersPanel && (
                        <div className="p-4 pt-2 border-t border-border/40 space-y-4">
                          {/* Tags */}
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                              <Tag size={12} /> Tags
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {['High-Protein', 'Low-Carb', 'Quick', 'Vegetarian', 'Gluten-Free', 'Dairy-Free'].map(function(tag) {
                                var isSelected = selectedTags.includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    onClick={function() {
                                      if (isSelected) {
                                        setSelectedTags(selectedTags.filter(function(t) { return t !== tag; }));
                                      } else {
                                        setSelectedTags([].concat(selectedTags, [tag]));
                                      }
                                    }}
                                    className={'px-2.5 py-1 rounded-full text-xs font-medium transition-colors ' + 
                                      (isSelected ? 'bg-accent text-white' : 'bg-secondary/50 hover:bg-secondary/70')}
                                    data-testid={'tag-' + tag}
                                  >
                                    {isSelected && <Check size={10} className="inline mr-1" />}
                                    {tag}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Cuisine */}
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Cuisine</p>
                            <div className="flex flex-wrap gap-1.5">
                              {['Mexican', 'Italian', 'Asian', 'American', 'Mediterranean'].map(function(cuisine) {
                                var isSelected = selectedCuisine.includes(cuisine);
                                return (
                                  <button
                                    key={cuisine}
                                    onClick={function() {
                                      if (isSelected) {
                                        setSelectedCuisine(selectedCuisine.filter(function(c) { return c !== cuisine; }));
                                      } else {
                                        setSelectedCuisine([].concat(selectedCuisine, [cuisine]));
                                      }
                                    }}
                                    className={'px-2.5 py-1 rounded-full text-xs font-medium transition-colors ' + 
                                      (isSelected ? 'bg-purple-500 text-white' : 'bg-secondary/50 hover:bg-secondary/70')}
                                    data-testid={'cuisine-' + cuisine}
                                  >
                                    {isSelected && <Check size={10} className="inline mr-1" />}
                                    {cuisine}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Clear filters */}
                          {(selectedTags.length > 0 || selectedCuisine.length > 0) && (
                            <button
                              onClick={function() { setSelectedTags([]); setSelectedCuisine([]); }}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* LEFTOVERS-LITE: Include leftovers (multi-day plans only —
                        getRegenDates().length >= 2 matters once a 1-day custom
                        range can put mode==='weekly'; normal Plan This
                        Week/Regenerate always have 7 dates here) */}
                    {leftoversFlagEnabled && !regenerateSingleDay && mode !== 'daily' && getRegenDates().length >= 2 && (
                      <button
                        type="button"
                        onClick={function() { setLeftoversTouched(true); setIncludeLeftovers(function(v) { return !v; }); }}
                        className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors text-left"
                        data-testid="toggle-include-leftovers-btn"
                        aria-pressed={includeLeftovers}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                            <Package size={20} className="text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">Include leftovers</p>
                            <p className="text-xs text-muted-foreground">
                              Plan select dinners with an extra serving for lunch the next day.
                            </p>
                          </div>
                          <div
                            className={'relative w-11 h-6 rounded-full transition-colors shrink-0 ' +
                              (includeLeftovers ? 'bg-accent' : 'bg-secondary')}
                          >
                            <div
                              className={'absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ' +
                                (includeLeftovers ? 'left-[22px]' : 'left-0.5')}
                            />
                          </div>
                        </div>
                      </button>
                    )}

                    <Button
                      onClick={handleProceedToGenerate}
                      className="w-full h-12 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90 mt-4"
                      data-testid="proceed-generate-btn"
                    >
                      <Wand2 size={18} className="mr-2" />
                      {regenerateSingleDay || mode === 'daily' ? 'Generate Plan' : 'Generate Week'}
                    </Button>
                    
                    <Button
                      onClick={function() { 
                        // Back from preferences: return to keep-selection if
                        // there are still-plannable meals to choose from,
                        // otherwise close the modal entirely (the legacy
                        // 'confirm' step is gone).
                        var stillPlannable = function(p) {
                          return p.status !== 'skipped' && p.status !== 'completed';
                        };
                        var mealsExist = regenerateSingleDay
                          ? getMealsForDate(regenerateSingleDay).filter(stillPlannable).length > 0
                          : mode === 'daily'
                            ? getMealsForDate(selectedDate).filter(stillPlannable).length > 0
                            : getRegenDates().some(function(d) {
                                return getMealsForRegenDate(d).filter(stillPlannable).length > 0;
                              });
                        if (mealsExist) {
                          setRegenerateStep('keep-selection');
                        } else {
                          handleCloseRegenerateModal();
                        }
                      }}
                      variant="ghost"
                      className="w-full h-10 rounded-xl text-muted-foreground"
                    >
                      Back
                    </Button>
                  </div>
                </>
              )}
              
              {/* Step 4: Cooking Preferences Editor */}
              {regenerateStep === 'cooking' && (
                <div className="max-h-[70vh] overflow-y-auto -mx-2 px-2">
                  <CookingPreferencesSelector
                    selectedCookware={selectedCookware}
                    selectedCookTime={selectedCookTime}
                    onSave={handleSaveCookingPrefs}
                    onBack={function() { setRegenerateStep('preferences'); }}
                  />
                </div>
              )}
              
              {/* Step 4b: Pantry Editor */}
              {regenerateStep === 'pantry' && (
                <div className="max-h-[70vh] overflow-y-auto -mx-2 px-2">
                  <h3 className="font-heading font-bold text-lg text-center mb-2">My Pantry</h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Select items you have available
                  </p>
                  <PantrySelector
                    selectedItems={pantryItems}
                    onItemsChange={setPantryItems}
                    onClose={function() { handleSavePantry(pantryItems); }}
                  />
                </div>
              )}
              
              {/* Step 5: Generating */}
              {regenerateStep === 'generating' && (
                <div className="py-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                    <div className="animate-spin w-8 h-8 border-3 border-accent border-t-transparent rounded-full" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">
                    Generating your plan...
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {mode === 'daily' ? 'Finding the best meals for your day' : 'Finding the best meals for your week'}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* A bottom-tab tap during Fix Days is a deliberate exit (Plan-on-Plan
          included: PlanningHub stays mounted, so its state must be reset
          here, not just sessionStorage). Never blocks navigation. */}
      <BottomNav beforeNavigate={function() { if (fixModeActive) exitFixMode(); }} />
    </div>
  );
}

export default PlanningHub;
